/* eslint-disable unicode-bom */
import React, { useState, useEffect, useRef, useMemo } from "react";
import SlotReel from "./components/SlotReel"; // SlotReelコンポーネントをインポート
import { linuxCommands } from "./data/linux_commands"; // Linux コマンド
import { sqlCommands } from "./data/sql_commands"; // SQL コマンド
import { gitCommands } from "./data/git_commands"; // Git コマンド
import { dockerCommands } from "./data/docker_commands"; // Docker コマンド
import { enemies, enemiesByCategory } from "./data/enemies";
import { getLinuxOptions } from "./data/linux_options";
import { getGitOptions } from "./data/git_options";
import { getDockerOptions } from "./data/docker_options";
import "./App.css";
import placeholderImg from "./assets/placeholder.svg";
import GlobalHeader from "./components/GlobalHeader";
import GlobalFooter from "./components/GlobalFooter";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import LicensePage from "./pages/LicensePage";
import CompanyPage from "./pages/CompanyPage";
import ContactPage from "./pages/ContactPage";
import HomePage from "./pages/HomePage";
import HowToPage from "./pages/HowToPage";
import IMobileAdIframe from "./components/IMobileAdIframe";
// GlobalMenuBar removed per request (header cleared)

const PUBLIC_PATH = process.env.PUBLIC_URL || '';

// ここでのグローバル設定はやめ、コンポーネント内でカテゴリに応じて同期する

const generateReelSymbols = (list) => {
  // 固定リールストリップ（1-based indexでyojijukugoListを参照）
  const leftReel = [1, 2, 3, 4, 3, 4, 5, 6, 4, 3, 4, 2, 7, 4, 3, 4, 6, 5, 4, 3, 4];
  const middleReel = [3, 2, 4, 6, 3, 1, 4, 6, 3, 5, 4, 6, 3, 1, 4, 6, 3, 5, 4, 6, 7];
  const rightReel = [4, 2, 5, 1, 3, 4, 7, 1, 3, 4, 7, 1, 3, 4, 7, 1, 3, 4, 7, 1, 3];

  const reels = [leftReel, middleReel, rightReel];
  const base = Array.isArray(list) ? list : [];
  const shuffledYojijukugoList = [...base].sort(() => Math.random() - 0.5);

  // 視認性の高い前景色（明るい背景#bfc7cf上でのテキストを想定）
  const palette = [
    '#1f77b4', // 濃い青
    '#d62728', // 濃い赤
    '#2ca02c', // 濃い緑
    '#9467bd', // 濃い紫
    '#ff7f0e', // 濃いオレンジ
    // '#17becf', // 濃いシアン（みずいろ系は視認しづらいので除外）
    '#4d4d4d', // ダークグレー（高コントラスト）
    '#000000', // 黒（最高コントラスト）
    '#8c564b', // 茶
    '#cc79a7'  // マゼンタ系ピンク
  ];

  // 3本のリール全体で使用されるコマンドに対し、安定した色を割り当てる
  const usedIndexSet = new Set(reels.flat());
  const sortedUsed = Array.from(usedIndexSet).sort((a, b) => a - b);
  const colorMap = new Map(); // text -> color
  let ptr = 0;
  for (const oneBased of sortedUsed) {
    const item = shuffledYojijukugoList[oneBased - 1];
    if (!item) continue;
    if (!colorMap.has(item.text)) {
      colorMap.set(item.text, palette[ptr % palette.length]);
      ptr++;
    }
  }

  // マッピングを適用
  const result = reels.map((reel, reelIndex) =>
    reel.map((idx1, symbolIndex) => {
      const item = shuffledYojijukugoList[idx1 - 1];
      if (!item) {
        console.error(`Missing item at index ${idx1 - 1} for reel ${reelIndex}, symbol ${symbolIndex}`);
        return { text: 'error', color: '#000000' };
      }
      const color = colorMap.get(item.text) || palette[0];
      return { text: item.text, color };
    })
  );

  // 念のためのガード：同一リール内で同じ色が連続していれば、継ぎ目以外は次色にずらす
  for (let r = 0; r < result.length; r++) {
    const arr = result[r];
    for (let i = 1; i < arr.length; i++) {
      if (arr[i]?.color && arr[i - 1]?.color && arr[i].color === arr[i - 1].color && arr[i].text !== arr[i - 1].text) {
        // 異なるコマンドなのに同色になった場合のみシフト（理論上起きにくいが保険）
        const curIdx = Math.max(0, palette.indexOf(arr[i].color));
        arr[i] = { ...arr[i], color: palette[(curIdx + 1) % palette.length] };
      }
    }
  }

  return result;
};

// スピン開始時点で各リールの停止インデックスを事前決定する関数（純粋関数）
// 引数: reelSymbols: Array<Array<{ text: string, color: string }>>
// 戻り値: 各リールの targetIndex（中段に来るインデックス）の配列 [n0, n1, n2]
const decideReelStopsAtSpinStart = (reelSymbols, rng = Math.random, gameState = {}) => {
  const random = rng();
  
  // 接戦バランス調整：プレイヤーの状況に応じて確率を調整
  const { playerHP = 100, enemyHP = 100, consecutiveLosses = 0 } = gameState;
  
  let winChance = 0.15; // 基本の正解確率 15% (調整: 12% → 15%)
  let matchChance = 0.25; // 基本の揃い（不正解含む）確率 25%
  
  // HPバランスによる調整
  if (playerHP < enemyHP - 30) {
    // プレイヤー不利時：正解確率を上げる
    winChance *= 1.8;
    matchChance *= 1.5;
  } else if (enemyHP < playerHP - 30) {
    // プレイヤー有利時：正解確率を下げる
    winChance *= 0.6;
    matchChance *= 0.8;
  }
  
  // 連続失敗による調整
  if (consecutiveLosses >= 2) {
    // 連続失敗時：救済措置として確率アップ
    winChance *= 2.0;
    matchChance *= 1.6;
  }
  
  // 現在の問題に対応する正解コマンドを特定
  const currentMondai = gameState.currentMondai;
  let correctCommandIndex = -1;
  if (currentMondai) {
    const found = window.yojijukugoList?.find?.(item => item.mondai === currentMondai);
    if (found) {
      correctCommandIndex = window.yojijukugoList.indexOf(found) + 1; // 1-based
    }
  }
  
  if (random < winChance && correctCommandIndex > 0) {
    // 正解を狙う：該当するコマンドが揃うように調整
    const stops = [];
    for (let reelIdx = 0; reelIdx < reelSymbols.length; reelIdx++) {
      const symbols = reelSymbols[reelIdx];
      const targetIndices = symbols
        .map((sym, idx) => ({ sym, idx }))
        .filter(({ sym }) => sym.text === window.yojijukugoList?.[correctCommandIndex - 1]?.text)
        .map(({ idx }) => idx);
      
      if (targetIndices.length > 0) {
        stops.push(targetIndices[Math.floor(rng() * targetIndices.length)]);
      } else {
        stops.push(Math.floor(rng() * symbols.length));
      }
    }
    
    try {
      // console.groupCollapsed("=== 事前決定: 正解狙い ===");
      stops.forEach((idx, reelIdx) => {
        // const sym = reelSymbols[reelIdx]?.[idx];
        // console.log(`リール${reelIdx + 1}: index=${idx} → "${sym?.text}" (正解狙い)`);
      });
      console.groupEnd();
    } catch (_) {}
    
    return stops;
  } else if (random < winChance + matchChance) {
    // 不正解だが揃いを狙う：ランダムなコマンドが揃うように
    const availableCommands = new Set();
    reelSymbols.forEach(reel => 
      reel.forEach(symbol => availableCommands.add(symbol.text))
    );
    const commandList = Array.from(availableCommands);
    const targetCommand = commandList[Math.floor(rng() * commandList.length)];
    
    const stops = [];
    for (let reelIdx = 0; reelIdx < reelSymbols.length; reelIdx++) {
      const symbols = reelSymbols[reelIdx];
      const targetIndices = symbols
        .map((sym, idx) => ({ sym, idx }))
        .filter(({ sym }) => sym.text === targetCommand)
        .map(({ idx }) => idx);
      
      if (targetIndices.length > 0) {
        stops.push(targetIndices[Math.floor(rng() * targetIndices.length)]);
      } else {
        stops.push(Math.floor(rng() * symbols.length));
      }
    }
    
    try {
      // console.groupCollapsed("=== 事前決定: 不正解揃い ===");
      stops.forEach((idx, reelIdx) => {
        // const sym = reelSymbols[reelIdx]?.[idx];
        // console.log(`リール${reelIdx + 1}: index=${idx} → "${sym?.text}" (不正解揃い)`);
      });
      console.groupEnd();
    } catch (_) {}
    
    return stops;
  } else {
    // 完全外れ：ランダム
    const randomStops = reelSymbols.map((symbols, rIdx) => {
      const L = Array.isArray(symbols) ? symbols.length : 0;
      return L > 0 ? Math.floor(rng() * L) : 0;
    });
    
    try {
      // console.groupCollapsed("=== 事前決定: ランダム外れ ===");
      randomStops.forEach((idx, reelIdx) => {
        // const sym = reelSymbols[reelIdx]?.[idx];
        // console.log(`リール${reelIdx + 1}: index=${idx} → "${sym?.text}" (外れ)`);
      });
      console.groupEnd();
    } catch (_) {}
    
    return randomStops;
  }
};

// アニメーション表示時間の定数
// ターミナル有効時: フェーズ1(コマンド/説明)→フェーズ2(ターミナル) 合計10秒(既存仕様)
// ターミナル無効時: 無意味な待ち時間を避けて早めに閉じる(ここでは4秒)
const PHASE1_DURATION_MS = 500;            // フェーズ1の長さ（発光0.5秒）
const PHASE2_ADDITIONAL_MS = 500;          // フェーズ2まで含めた追加時間(合計1.0秒)

const App = () => {
  // 新規: ゲームモード/フェーズ/敵選択（連戦モード廃止のためステート不要）
  // gamePhase: 'menu' | 'select' | 'intro' | 'battle' | 'win' | 'lose' | 'ending'
  const [gamePhase, setGamePhase] = useState('menu');
  const [currentEnemyIndex, setCurrentEnemyIndex] = useState(0);
  // currentEnemy は gameCategory 宣言後に決定
  // 追加: カテゴリ選択（Linux/SQL/git/docker）
  // gameCategory: 'linux' | 'sql' | 'git' | 'docker' | null（未選択）
  const [gameCategory, setGameCategory] = useState(null);
  // 新規: カテゴリ選択直後に表示するモード選択のため、カテゴリを一時保持
  const [pendingCategory, setPendingCategory] = useState(null);
  // 新規: 操作モード（手動/放置）
  // null の間はモード未選択
  const [playMode, setPlayMode] = useState(null); // 'manual' | 'auto' | null
  const categoryNames = {
    linux: 'Linux',
    sql: 'SQL',
    git: 'Git',
    docker: 'Docker'
  };
  const currentCategoryName = gameCategory ? (categoryNames[gameCategory] || String(gameCategory)) : '';
  // モバイル下部バナー：勝敗後に「もう一度遊ぶ」(カテゴリ選択→再開)した時に再描画するためのキー
  // const [mobileBottomBannerKey, setMobileBottomBannerKey] = useState(0); // バナー広告撤去のため未使用
  // const prevGameCategoryRef = useRef(null); // バナー広告撤去のため未使用
  // カテゴリに応じた敵リスト（未選択時は従来の enemies を使用）
  const availEnemies = useMemo(() => {
    if (!gameCategory) return enemies;
    const list = enemiesByCategory?.[gameCategory];
    return Array.isArray(list) && list.length > 0 ? list : enemies;
  }, [gameCategory]);
  const currentEnemy = availEnemies[Math.max(0, Math.min(availEnemies.length - 1, currentEnemyIndex))];
  // アクティブな学習データ（カテゴリに応じて切替）
  const yojijukugoList = useMemo(() => {
    switch (gameCategory) {
      case 'sql':
        return sqlCommands;
      case 'git':
        return gitCommands;
      case 'docker':
        return dockerCommands;
      case 'linux':
      default:
        return linuxCommands;
    }
  }, [gameCategory]);
  // window へも同期（事前決定/正解判定ロジックが参照）
  useEffect(() => {
    try { if (typeof window !== 'undefined') { window.yojijukugoList = yojijukugoList; } } catch(_) {}
  }, [yojijukugoList]);

  // カテゴリが未選択→選択に変わったタイミングを「新しいプレイ開始」とみなし、下部バナーを再マウント
  // useEffect(() => {
  //   const prev = prevGameCategoryRef.current;
  //   if (!prev && gameCategory) {
  //     setMobileBottomBannerKey((k) => k + 1);
  //   }
  //   prevGameCategoryRef.current = gameCategory;
  // }, [gameCategory]);
  // 問題切り替えフラグ
  const [shouldSwitchMondai, setShouldSwitchMondai] = useState(false);
  // プレイヤー攻撃アニメーション用
  const [playerAttackAnim, setPlayerAttackAnim] = useState(false);
  // 敵攻撃アニメーション用
  const [enemyAttackAnim, setEnemyAttackAnim] = useState(false);
  // 攻撃アニメーションの種類 ('lightning', 'fire', 'ice', 'slash', 'impact')
  const [attackAnimType, setAttackAnimType] = useState('lightning');
  // 揃い絵柄（点滅中コマンド）を保存
  const [matchedText, setMatchedText] = useState("");
  // まずreelSymbolsを初期化（再戦や相手変更時に再生成できるようStateで管理）
  const [reelSymbols, setReelSymbols] = useState(() => generateReelSymbols(yojijukugoList));
  // 現在リールで使われている絵柄（コマンド）のmondaiから問題を出す
  const getCurrentReelCommands = React.useCallback(() => {
    const commands = new Set();
    reelSymbols.forEach(reel => reel.forEach(symbol => commands.add(symbol.text)));
    return Array.from(commands);
  }, [reelSymbols]);
  const getMondaiFromCommands = React.useCallback(() => {
    const commands = getCurrentReelCommands();
    return yojijukugoList.filter(item => commands.includes(item.text) && item.mondai).map(item => item.mondai);
  }, [getCurrentReelCommands, yojijukugoList]);
  const [currentMondai, setCurrentMondai] = useState("");
  const [mondaiSwitchFxNonce, setMondaiSwitchFxNonce] = useState(0); // 問題切替時の発光（アニメ再始動用）
  const [mondaiSwitchBorderFlash, setMondaiSwitchBorderFlash] = useState(false); // 問題切替時の枠発光（太枠）
  useEffect(() => {
    const mondais = getMondaiFromCommands();
    let filtered = mondais.filter(m => m !== currentMondai);
    if (filtered.length === 0) filtered = mondais;
    setCurrentMondai(filtered.length > 0 ? filtered[Math.floor(Math.random() * filtered.length)] : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMondaiFromCommands]);

  // 問題が切り替わったら、問題文表示欄全体を一瞬発光させる
  useEffect(() => {
    if (!currentMondai) return;
    setMondaiSwitchFxNonce((n) => n + 1);
    setMondaiSwitchBorderFlash(true);
    const t = setTimeout(() => setMondaiSwitchBorderFlash(false), 520);
    return () => clearTimeout(t);
  }, [currentMondai]);

  // カテゴリ変更時にリール・問題をリセット
  useEffect(() => {
    // gameCategory が null（未選択）のときは何もしない
    if (!gameCategory) return;
    try { setReelSymbols(generateReelSymbols(yojijukugoList)); } catch(_) {}
    // setHighlightIndexes([]); // START/画面遷移以外で消さない
    setEuphoriaActive(false); // カテゴリ変更時にKWSK発光を必ず停止
    setIsCorrectAnswer(false); // カテゴリ変更時にリール発光も必ず停止
    setMatchedText("");
    setTargetIndexes([null, null, null]);
    setSpinning([false, false, false]);
    setPayout(0);
    setHasSpun(false);
    setSuppressBorderGlow(false);
    setPredeterminedResults([null, null, null]);
    setPoopCount(0);
    setPatiencePoints(0);
    setGarlicBlinking(false);
    setPoopBlinking(false);
    setShowKwsk(false);
    setKwskEntry(null);
    setCurrentMondai("");
    // HP初期化とフェーズ遷移をここで行うことで、yojijukugoList更新後のリールに基づく問題が確実に生成される
    setPlayerHP(100);
    setEnemyHP(100);
    setPlayerDisplayHP(100);
    setEnemyDisplayHP(100);
    setShowAnimation(false);
    setGamePhase('battle');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameCategory]);

  // カテゴリ未選択に戻ったら、モード/仮カテゴリもクリア
  useEffect(() => {
    if (gameCategory) return;
    setPendingCategory(null);
    setPlayMode(null);
  }, [gameCategory]);
  // バトル仕様用ステート
    // カテゴリ選択画面に遷移したとき、リールとKWSKの発光を必ず停止
    useEffect(() => {
      if (gamePhase === 'select' || gamePhase === 'battle') {
        setEuphoriaActive(false); // KWSK発光を必ず停止
        setIsCorrectAnswer(false); // リール発光も必ず停止
        setHighlightIndexes([]);
        setHighlightIsCorrect(null);
        setShowKwsk(false);
      }
    }, [gamePhase]);
  const [playerHP, setPlayerHP] = useState(100);
  const [enemyHP, setEnemyHP] = useState(100);
  // HPアニメーション用
  const [playerDisplayHP, setPlayerDisplayHP] = useState(100);
  const [enemyDisplayHP, setEnemyDisplayHP] = useState(100);
  // HP変動時の演出状態
  const [hpEffect, setHpEffect] = useState(null); // 'player-damage' | 'player-heal' | 'enemy-damage' | 'enemy-heal' | null
  const prevPlayerHPRef = useRef(playerHP);
  const prevEnemyHPRef = useRef(enemyHP);

  // プレイヤー/敵のHP変化を検知して一時的な演出フラグを立てる
  // (フック本体は `poopExploding` 宣言後へ移動しています)

  // 色覚サポート（CVD）ON/OFF：ミニメニューから切替可能、ローカルに保存
  const [a11yCvd, setA11yCvd] = useState(() => {
    try { return localStorage.getItem('a11yCvd') === '1'; } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('a11yCvd', a11yCvd ? '1' : '0'); } catch (_) {}
  }, [a11yCvd]);

  // グローバルメニューからの色覚サポート制御（body class: accessibility-on）を監視し、a11yCvdと同期
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isAccessOn = document.body.classList.contains('accessibility-on');
      setA11yCvd(isAccessOn);
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // 低刺激モード（フラッシュ/シェイクを抑制）：ミニメニューから切替、ローカル保存
  const [a11yLowStim, setA11yLowStim] = useState(() => {
    try { return localStorage.getItem('a11yLowStim') === '1'; } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('a11yLowStim', a11yLowStim ? '1' : '0'); } catch (_) {}
  }, [a11yLowStim]);

  // グローバルメニューからの低刺激モード制御（body class: low-stim-on）を監視し、a11yLowStimと同期
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isLowStimOn = document.body.classList.contains('low-stim-on');
      setA11yLowStim(isLowStimOn);
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // 初回起動時の既定値を明示的に設定（localStorageキーの存在がない場合）
  useEffect(() => {
    try {
      if (localStorage.getItem('a11yCvd') === null) localStorage.setItem('a11yCvd', '0');
      if (localStorage.getItem('colorSupport') === null) localStorage.setItem('colorSupport', '0');
      if (localStorage.getItem('a11yLowStim') === null) localStorage.setItem('a11yLowStim', '0');
      if (localStorage.getItem('lowStim') === null) localStorage.setItem('lowStim', '0');
      if (localStorage.getItem('bgmEnabled') === null) localStorage.setItem('bgmEnabled', '0');
      if (localStorage.getItem('sfxEnabled') === null) localStorage.setItem('sfxEnabled', '1');
      if (localStorage.getItem('autoPlaySpeed') === null) localStorage.setItem('autoPlaySpeed', 'normal');
      // body / documentElement のクラスをデフォルトに合わせる
      try { document.body.classList.toggle('accessibility-on', false); } catch (_) {}
      try { document.body.classList.toggle('low-stim-on', false); } catch (_) {}
      try { document.body.classList.toggle('bgm-off', localStorage.getItem('bgmEnabled') === '0'); } catch (_) {}
      try { document.body.classList.toggle('sfx-off', localStorage.getItem('sfxEnabled') === '0'); } catch (_) {}
      try { document.documentElement.classList.toggle('color-support', false); } catch (_) {}
      try { document.documentElement.classList.toggle('low-stim', false); } catch (_) {}
    } catch (_) {}
  }, []);
  // const [bet, setBet] = useState(false); // BETボタン廃止のため不要
  const [spinning, setSpinning] = useState([false, false, false]); // 各リールの回転状態
  const [bgmPlaying, setBgmPlaying] = useState(() => {
    try {
      const v = localStorage.getItem('bgmEnabled');
      if (v === null) return !document.body.classList.contains('bgm-off');
      return v === '1';
    } catch (_) {
      return !document.body.classList.contains('bgm-off');
    }
  });
  const [sfxPlaying, setSfxPlaying] = useState(() => {
    try {
      const v = localStorage.getItem('sfxEnabled');
      if (v === null) return !document.body.classList.contains('sfx-off');
      return v === '1';
    } catch (_) {
      return !document.body.classList.contains('sfx-off');
    }
  });
  // Terminalトグルは廃止
  const [coins, setCoins] = useState(50); // 初期コイン枚数
  const [payout, setPayout] = useState(0); // 払い出し枚数
  const [targetIndexes, setTargetIndexes] = useState([null, null, null]);
  const [predeterminedResults, setPredeterminedResults] = useState([null, null, null]); // スピン開始時に決定される最終停止位置
  const [, setShowAnimation] = useState(false); // アニメーション表示状態（値は未使用）
  // 旧ターミナル用のステートは撤去
  // 旧ターミナルカーソル状態は撤去
  const [highlightIndexes, setHighlightIndexes] = useState([]); // 光らせる絵柄のインデックス
  const [hasSpun, setHasSpun] = useState(false); // リールが一度でも回転したかどうかを管理
  // 旧ダブルタップ検出は撤去 
  const [consecutiveLosses, setConsecutiveLosses] = useState(0);
  const [patiencePoints, setPatiencePoints] = useState(0); // にんにく（忍耐）ポイント
  const [poopCount, setPoopCount] = useState(0); // うんこマークカウント
  const [garlicBlinking, setGarlicBlinking] = useState(false); // にんにくポイント点滅状態
  const [poopBlinking, setPoopBlinking] = useState(false); // うんこマーク点滅状態
  const [poopExploding, setPoopExploding] = useState(false); // うんこ爆発エフェクト
  // プレイヤー/敵のHP変動を監視して演出フラグを立てる（うんこ爆発時は全画面赤フラッシュを抑制）
  useEffect(() => {
    let timeoutId = null;
    try {
      if (prevPlayerHPRef.current !== playerHP) {
        // うんこ爆発時は別演出（.poop-explosion）で処理するため、全画面赤フラッシュを抑制する
        if (poopExploding) {
          prevPlayerHPRef.current = playerHP;
        } else {
          if (playerHP < prevPlayerHPRef.current) {
            setHpEffect('player-damage');
          } else {
            setHpEffect('player-heal');
          }
          prevPlayerHPRef.current = playerHP;
          timeoutId = setTimeout(() => setHpEffect(null), 650);
        }
      }
      if (prevEnemyHPRef.current !== enemyHP) {
        if (enemyHP < prevEnemyHPRef.current) {
          setHpEffect('enemy-damage');
        } else {
          setHpEffect('enemy-heal');
        }
        prevEnemyHPRef.current = enemyHP;
        // 上書きされるのを避けるため少し長め
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => setHpEffect(null), 650);
      }
    } catch (e) {
      // noop
    }
    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, [playerHP, enemyHP, poopExploding]);
  const [isCountingUp, setIsCountingUp] = useState(false); // コインカウントアップ中の状態
  const [reelResetNonce, setReelResetNonce] = useState(0); // リール再マウント用
  const [isCorrectAnswer, setIsCorrectAnswer] = useState(false); // 正解時のみ発光させるための状態
  const [mondaiCorrectFlash, setMondaiCorrectFlash] = useState(false); // 正解成立時に問題文を黄色で一瞬フラッシュ
  useEffect(() => {
    if (!isCorrectAnswer) return;
    setMondaiCorrectFlash(true);
    const t = setTimeout(() => setMondaiCorrectFlash(false), 900);
    return () => clearTimeout(t);
  }, [isCorrectAnswer]);
  // 正解後〜次のスピン開始まで外枠発光を持続させるフラグ
  const [euphoriaActive, setEuphoriaActive] = useState(false);
  const [suppressBorderGlow, setSuppressBorderGlow] = useState(false); // 不正解そろい時に周囲の発光を抑制
  const [highlightIsCorrect, setHighlightIsCorrect] = useState(null); // 現在のハイライトが正解由来かどうか
  const [attackingCommand, setAttackingCommand] = useState(null); // 正解時に敵を攻撃するコマンド表示用

  // 統計データ（カテゴリ→敵名別、localStorage使用）
  const [stats, setStats] = useState(() => {
    try {
      const saved = localStorage.getItem('slotStats');
      if (saved) {
        const parsed = JSON.parse(saved);
        // 旧形式から新形式への移行
        if (!parsed.byCategory) {
          return {
            byCategory: {
              linux: {},
              sql: {},
              git: {},
              docker: {}
            },
            currentWinStreak: 0,
            currentLoseStreak: 0
          };
        }
        // カテゴリ直下に統計があった場合、敵名ごとに分割する必要があるが、
        // 敵名が分からないため、新形式として扱う
        if (parsed.byCategory) {
          Object.keys(parsed.byCategory).forEach(cat => {
            // 旧形式（totalSpinsが直接ある）の場合は新形式に変換
            if (typeof parsed.byCategory[cat].totalSpins === 'number') {
              parsed.byCategory[cat] = {};
            }
          });
        }
        return parsed;
      }
    } catch {
      // エラー時はデフォルト値
    }
    return {
      byCategory: {
        linux: {},
        sql: {},
        git: {},
        docker: {}
      },
      currentWinStreak: 0,
      currentLoseStreak: 0
    };
  });
  // アンティシペーション（2個目まで揃って残り1リール時の演出）
  const [anticipationActive, setAnticipationActive] = useState(false);
  const [anticipationReelIndex, setAnticipationReelIndex] = useState(null);
  // kwsk(詳しく)表示用
  const [showKwsk, setShowKwsk] = useState(false);
  const [kwskEntry, setKwskEntry] = useState(null);
  const kwskCardRef = useRef(null);
  const animationPanelRef = useRef(null);
  const enemyStatusRef = useRef(null);
  const statusMondaiStackRef = useRef(null);
  const [lastCorrectEntry, setLastCorrectEntry] = useState(null); // 直前の正解コマンド
  const endTransitionTimerRef = useRef(null); // 勝敗遷移のディレイ管理
  // 放置モード: 勝敗確定時に勝敗画面へ行く前にOKポップアップを出す
  const [showAutoPlayEndModal, setShowAutoPlayEndModal] = useState(false);
  const [autoPlayEndTargetPhase, setAutoPlayEndTargetPhase] = useState(null); // 'win' | 'lose' | null
  // 放置モード: 開始時にOKポップアップを出し、OK押下まで自動操作を開始しない
  const [showAutoPlayStartModal, setShowAutoPlayStartModal] = useState(false);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(true);

  // 放置速度（低速/中速/高速）。中速は現状のスピード。
  const [autoPlaySpeed, setAutoPlaySpeed] = useState(() => {
    try {
      const v = localStorage.getItem('autoPlaySpeed');
      return (v === 'slow' || v === 'fast' || v === 'normal') ? v : 'normal';
    } catch (_) {
      return 'normal';
    }
  });
  const autoPlaySpeedRef = useRef(autoPlaySpeed);
  useEffect(() => { autoPlaySpeedRef.current = autoPlaySpeed; }, [autoPlaySpeed]);
  // ミニメニュー表示制御
  const [showMiniMenu, setShowMiniMenu] = useState(false);
  const [miniMenuKbIndex, setMiniMenuKbIndex] = useState(0);
  const miniMenuRef = useRef(null);
  // 利用規約など情報表示用
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyType, setPolicyType] = useState(null); // 'terms' | 'privacy' | 'reporting'
  // ポリシーモーダル表示中のキーボード制御（Enter / Escape / Backspace で閉じる）
  useEffect(() => {
    if (!showPolicyModal) return;
    const onKey = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return; // 入力中は許可
      if (['Enter','Escape','Backspace'].includes(e.key)) {
        e.preventDefault();
        setShowPolicyModal(false);
        setPolicyType(null);
        return;
      }
      // その他のキーはゲーム操作に伝播させない（閲覧中の誤操作防止）
      e.preventDefault();
    };
    // capture=true で他のグローバルハンドラより先に抑制
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [showPolicyModal]);

  // バトル風UI（初代ポケモン風）用の簡易ステータス

  const bgmRef = useRef(null);
  const hasSpunRef = useRef(false);
  const gamePhaseRef = useRef('');
  useEffect(() => { hasSpunRef.current = hasSpun; }, [hasSpun]);
  useEffect(() => { gamePhaseRef.current = gamePhase; }, [gamePhase]);
  // 放置モード解除（手動/未選択へ変更）時は、放置系ポップアップ/自動進行をリセット
  // ※モード選択直後は gamePhase が battle でないため、gamePhase で判定すると即閉じてしまう
  useEffect(() => {
    if (playMode === 'auto') return;
    setShowAutoPlayEndModal(false);
    setAutoPlayEndTargetPhase(null);
    setShowAutoPlayStartModal(false);
    setAutoPlayEnabled(true);
  }, [playMode]);
  const payoutCounterRef = useRef({ counting: false, current: 0, target: 0 });
  // スマホで“ホバー相当”の押下フィードバックを出すための .pressed 付与/除去
  useEffect(() => {
    const addPressed = (e) => {
      const el = e.target?.closest?.('button');
      if (!el || el.disabled) return;
      el.classList.add('pressed');
    };
    const removePressed = () => {
      document.querySelectorAll('button.pressed').forEach((b) => b.classList.remove('pressed'));
    };
    document.addEventListener('touchstart', addPressed, { passive: true });
    document.addEventListener('touchend', removePressed, { passive: true });
    document.addEventListener('touchcancel', removePressed, { passive: true });
    return () => {
      document.removeEventListener('touchstart', addPressed);
      document.removeEventListener('touchend', removePressed);
      document.removeEventListener('touchcancel', removePressed);
    };
  }, []);
  // モバイルの遅延タップや二重発火対策：最新のspinning状態を参照するためのref
  const spinningRef = useRef(spinning);
  useEffect(() => { spinningRef.current = spinning; }, [spinning]);

  // 放置モード: 正解成立時に次の自動操作を少し待つためのタイムスタンプ(ms)
  const autoPlayPauseUntilRef = useRef(0);

  // HPバーの色を決定（通常時は 50%未満=黄、25%未満=赤、50%以上=シアン）
  const getHPBarColor = (hp) => {
    // CVD（色覚サポート）ON時は色相差よりも明度差で統一（ブルー系）
    if (a11yCvd) {
      if (hp <= 25) return '#007aa6'; // ダークシアン
      if (hp <= 50) return '#00b0e0'; // ビビッドシアン
      return '#21dcff';               // 明るいシアン
    }
    if (hp <= 25) return '#ff3b3b';   // ネオンレッド
    if (hp <= 50) return '#ffd83b';   // ネオンイエロー
    return '#35e4ff';                 // ネオンサイアン
  };

  // HPバーのネオンスタイル用クラス（グラデ/装飾）
  const getHPNeonClass = (hp) => {
    if (a11yCvd) return 'neon-blue';
    if (hp <= 25) return 'neon-red';
    if (hp <= 50) return 'neon-yellow';
    return 'neon-blue';
  };

  // HPバーのシャドウ色を決定する関数（色相も段階で切り替え）
  const getHPBarShadow = (hp) => {
    // CVD ON時は白ベースの内側グローで色依存を下げる
    if (a11yCvd) {
      return '0 0 12px rgba(255, 255, 255, 0.6) inset';
    }
    if (hp <= 25) return '0 0 12px rgba(255, 68, 68, 0.65) inset';   // 赤
    if (hp <= 50) return '0 0 12px rgba(255, 210, 64, 0.65) inset'; // 黄
    return '0 0 12px rgba(0, 223, 255, 0.6) inset';                 // シアン
  };

  // HPアニメーション関数
  const animateHP = (fromHP, toHP, setDisplayHP) => {
  const duration = 300; // アニメーション時間（ミリ秒）
    const startTime = Date.now();
    const difference = toHP - fromHP;
    let lastEmitted = fromHP;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // イージングアウト（最初速く、後でゆっくり）
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentHP = Math.round(fromHP + (difference * easeOut));
      // 毎フレーム setState するとスマホでレンダリング負荷が上がりやすいので、値が変わった時だけ反映
      if (currentHP !== lastEmitted) {
        lastEmitted = currentHP;
        setDisplayHP(currentHP);
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // 端数でズレた時のため最後に確定
        if (lastEmitted !== toHP) setDisplayHP(toHP);
      }
    };
    
    requestAnimationFrame(animate);
  };

  // 音声ファイルを準備
  const leverSoundRef = useRef();
  const stopSoundRef = useRef();
  // モバイル向け: 連打/多重再生で pause/currentTime/cloneNode が重くなりやすいので、固定数のプールを回す
  const leverSoundPoolRef = useRef({ nodes: [], idx: 0 });
  const stopSoundPoolRef = useRef({ nodes: [], idx: 0 });
  const coingetLongSoundRef = useRef();
  const coingetSuperLongSoundRef = useRef();
  const attackSoundRef = useRef();
  const threeUnkoSoundRef = useRef();
  const decideSoundRef = useRef();
  const battleStartSoundRef = useRef();
  const returnSoundRef = useRef();
  const hpChargeSoundRef = useRef();
  const heartbeatLoopRef = useRef();
  // 勝利/敗北音
  const youWinSoundRef = useRef();
  const youLoseSoundRef = useRef();
  // アニメーションタイマー管理用
  const hideTimerRef = useRef(null);
  const reelHitAudioRef = useRef(null);
  // 停止ボタングループ参照 & 追加ミニボタン位置計算用
  const stopButtonsContainerRef = useRef(null);
  // リールチェンジボタンはコントロールパネルへ移設したため、位置計算は不要に
  // 右追加ボタン（停止ボタン群の右横）用の固定オフセット

  useEffect(() => {
  // スマホ軽量化: preload='none'でメモリ節約、オンデマンドロード
  // NOTE: new Audio(url) だと src 設定時点で読み込みが走ることがあるため、preload 設定→src の順にする。
  leverSoundRef.current = new Audio();
  leverSoundRef.current.preload = 'none';
  leverSoundRef.current.src = `${PUBLIC_PATH}/sounds/lever.mp3`;
  stopSoundRef.current = new Audio();
  stopSoundRef.current.preload = 'none';
  stopSoundRef.current.src = `${PUBLIC_PATH}/sounds/stop.mp3`;
  coingetLongSoundRef.current = new Audio();
  coingetLongSoundRef.current.preload = 'none';
  coingetLongSoundRef.current.src = `${PUBLIC_PATH}/sounds/coinget_long.mp3`;
  coingetSuperLongSoundRef.current = new Audio();
  coingetSuperLongSoundRef.current.preload = 'none';
  coingetSuperLongSoundRef.current.src = `${PUBLIC_PATH}/sounds/coinget_superlong.mp3`;
  attackSoundRef.current = new Audio();
  attackSoundRef.current.preload = 'none';
  attackSoundRef.current.src = `${PUBLIC_PATH}/sounds/attack.mp3`;
  threeUnkoSoundRef.current = new Audio();
  threeUnkoSoundRef.current.preload = 'none';
  threeUnkoSoundRef.current.src = `${PUBLIC_PATH}/sounds/three_unko.mp3`;
  decideSoundRef.current = new Audio();
  decideSoundRef.current.preload = 'none';
  decideSoundRef.current.src = `${PUBLIC_PATH}/sounds/decide.mp3`;
  battleStartSoundRef.current = new Audio();
  battleStartSoundRef.current.preload = 'none';
  battleStartSoundRef.current.src = `${PUBLIC_PATH}/sounds/battle_start.mp3`;
  returnSoundRef.current = new Audio();
  returnSoundRef.current.preload = 'none';
  returnSoundRef.current.src = `${PUBLIC_PATH}/sounds/return.mp3`;
  hpChargeSoundRef.current = new Audio();
  hpChargeSoundRef.current.preload = 'none';
  hpChargeSoundRef.current.src = `${PUBLIC_PATH}/sounds/hp_charge.mp3`;
  youWinSoundRef.current = new Audio();
  youWinSoundRef.current.preload = 'none';
  youWinSoundRef.current.src = `${PUBLIC_PATH}/sounds/you_win.mp3`;
  youLoseSoundRef.current = new Audio();
  youLoseSoundRef.current.preload = 'none';
  youLoseSoundRef.current.src = `${PUBLIC_PATH}/sounds/you_lose.mp3`;

  // 主要SEはプールを準備（cloneNodeを避ける）
  try {
    const makePool = (src, size, volume) => {
      const nodes = [];
      for (let i = 0; i < size; i++) {
        try {
          const a = new Audio();
          a.preload = 'none';
          a.src = src;
          try { a.volume = volume; } catch (_) {}
          nodes.push(a);
        } catch (_) {}
      }
      return { nodes, idx: 0 };
    };
    leverSoundPoolRef.current = makePool(`${PUBLIC_PATH}/sounds/lever.mp3`, 2, 0.3);
    stopSoundPoolRef.current = makePool(`${PUBLIC_PATH}/sounds/stop.mp3`, 3, 0.7);
  } catch (_) {}
    try {
  heartbeatLoopRef.current = new Audio();
  heartbeatLoopRef.current.preload = 'none';
  heartbeatLoopRef.current.src = `${PUBLIC_PATH}/sounds/heartbeat_loop.mp3`;
  heartbeatLoopRef.current.loop = true;
  heartbeatLoopRef.current.volume = 0.35;
    } catch(_) { heartbeatLoopRef.current = null; }
    
    // デバッグ: 初期化したAudioのsrcを表示（ローカル確認用）
    try {
      console.debug('[Audio init]', {
        lever: leverSoundRef.current?.src,
        stop: stopSoundRef.current?.src,
        coingetLong: coingetLongSoundRef.current?.src,
        coingetSuperLong: coingetSuperLongSoundRef.current?.src,
        attack: attackSoundRef.current?.src,
        threeUnko: threeUnkoSoundRef.current?.src,
        decide: decideSoundRef.current?.src,
        battleStart: battleStartSoundRef.current?.src,
        returnSound: returnSoundRef.current?.src,
        hpCharge: hpChargeSoundRef.current?.src,
        heartbeat: heartbeatLoopRef.current?.src,
      });
    } catch(_) {}

    // 音量設定
    try { if (leverSoundRef.current) leverSoundRef.current.volume = 0.3; } catch(e){}
    try { if (stopSoundRef.current) stopSoundRef.current.volume = 0.3; } catch(e){}
    try { if (coingetLongSoundRef.current) coingetLongSoundRef.current.volume = 0.4; } catch(e){}
    try { if (coingetSuperLongSoundRef.current) coingetSuperLongSoundRef.current.volume = 0.4; } catch(e){}
    try { if (attackSoundRef.current) attackSoundRef.current.volume = 0.5; } catch(e){}
    try { if (threeUnkoSoundRef.current) threeUnkoSoundRef.current.volume = 0.6; } catch(e){}
    try { if (decideSoundRef.current) decideSoundRef.current.volume = 0.6; } catch(e){}
    try { if (battleStartSoundRef.current) battleStartSoundRef.current.volume = 0.65; } catch(e){}
    try { if (youWinSoundRef.current) youWinSoundRef.current.volume = 0.7; } catch(e){}
    try { if (youLoseSoundRef.current) youLoseSoundRef.current.volume = 0.7; } catch(e){}
    
    // 使用後のメモリ解放
    return () => {
      [leverSoundRef, stopSoundRef, coingetLongSoundRef, coingetSuperLongSoundRef, 
       attackSoundRef, threeUnkoSoundRef, decideSoundRef, battleStartSoundRef, 
       returnSoundRef, hpChargeSoundRef, heartbeatLoopRef].forEach(ref => {
        try { if (ref.current) { ref.current.pause(); ref.current.src = ''; } } catch(_) {}
      });
      try {
        const pools = [leverSoundPoolRef, stopSoundPoolRef];
        pools.forEach(p => {
          const nodes = p?.current?.nodes;
          if (!Array.isArray(nodes)) return;
          nodes.forEach(n => {
            try { if (n && !n.paused) n.pause(); } catch(_) {}
            try { if (n) n.src = ''; } catch(_) {}
          });
        });
      } catch(_) {}
    };
  }, []);

  const canPlaySfx = React.useCallback(() => {
    try {
      if (!sfxPlaying) return false;
      if (document.body.classList.contains('sfx-off')) return false;
      return true;
    } catch (_) {
      return !!sfxPlaying;
    }
  }, [sfxPlaying]);

  // モバイルでHTMLAudioElementのpause/currentTime/playが重くなりやすいため、
  // よく鳴るSEはWebAudio(デコード済みバッファ)で鳴らせるようにする。
  const webAudioRef = useRef({ ctx: null, buffers: new Map(), inflight: new Map() });

  const ensureWebAudio = React.useCallback(() => {
    try {
      if (typeof window === 'undefined') return null;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      let ctx = webAudioRef.current.ctx;
      if (!ctx) {
        ctx = new Ctx();
        webAudioRef.current.ctx = ctx;
      }
      // ユーザー操作のハンドラ内だとresumeできることが多い（失敗しても次回でOK）
      if (ctx.state === 'suspended') {
        try { ctx.resume().catch(() => {}); } catch (_) {}
      }
      return ctx;
    } catch (_) {
      return null;
    }
  }, []);

  const preloadWebAudioBuffer = React.useCallback((src) => {
    try {
      if (!src) return;
      if (typeof src !== 'string') return;
      const ctx = ensureWebAudio();
      if (!ctx) return;
      if (webAudioRef.current.buffers.has(src)) return;
      if (webAudioRef.current.inflight.has(src)) return;

      const p = (async () => {
        try {
          const res = await fetch(src, { cache: 'force-cache' });
          const buf = await res.arrayBuffer();
          const audioBuf = await new Promise((resolve, reject) => {
            try {
              // Safari向けにコールバック形式も許容
              const r = ctx.decodeAudioData(buf, resolve, reject);
              if (r && typeof r.then === 'function') r.then(resolve).catch(reject);
            } catch (e) {
              reject(e);
            }
          });
          if (audioBuf) webAudioRef.current.buffers.set(src, audioBuf);
        } catch (_) {
        } finally {
          webAudioRef.current.inflight.delete(src);
        }
      })();

      webAudioRef.current.inflight.set(src, p);
    } catch (_) {}
  }, [ensureWebAudio]);

  const tryPlayWebAudioSfx = React.useCallback((src, volume = 1) => {
    try {
      if (!canPlaySfx()) return false;
      if (!src) return false;
      const ctx = ensureWebAudio();
      if (!ctx) return false;
      if (ctx.state !== 'running') {
        // resume中の可能性があるので、先にデコードだけ走らせておく
        preloadWebAudioBuffer(src);
        return false;
      }

      const audioBuf = webAudioRef.current.buffers.get(src);
      if (!audioBuf) {
        preloadWebAudioBuffer(src);
        return false;
      }

      const gain = ctx.createGain();
      gain.gain.value = Math.max(0, Math.min(1, typeof volume === 'number' ? volume : 1));
      gain.connect(ctx.destination);

      const source = ctx.createBufferSource();
      source.buffer = audioBuf;
      source.connect(gain);
      try { source.start(0); } catch (_) { return false; }
      return true;
    } catch (_) {
      return false;
    }
  }, [canPlaySfx, ensureWebAudio, preloadWebAudioBuffer]);

  const playSfxNode = React.useCallback((node, { restart = true } = {}) => {
    if (!canPlaySfx()) return;
    if (!node) return;
    try {
      // WebAudioで再生できる場合は、HTMLAudioのシーク/再生を避けて軽量化
      const src = node?.src;
      const vol = (() => { try { return node?.volume ?? 1; } catch (_) { return 1; } })();
      if (src && tryPlayWebAudioSfx(src, vol)) return;

      if (restart) {
        try {
          // 再生中でも currentTime を戻すだけで済むケースが多い
          node.currentTime = 0;
        } catch (_) {
          try { node.pause(); } catch (_) {}
          try { node.currentTime = 0; } catch (_) {}
        }
      }
      node.play().catch(() => {});
    } catch (_) {}
  }, [canPlaySfx, tryPlayWebAudioSfx]);

  const playSfxFromPool = React.useCallback((poolRef, { restart = true } = {}) => {
    if (!canPlaySfx()) return;
    try {
      const pool = poolRef?.current;
      const nodes = pool?.nodes;
      if (!Array.isArray(nodes) || nodes.length === 0) return;
      const idx = (pool.idx || 0) % nodes.length;
      pool.idx = (idx + 1) % nodes.length;
      const node = nodes[idx];
      if (!node) return;

      // WebAudioで再生できる場合はそちらを優先（高頻度SEの負荷軽減）
      const src = node?.src;
      const vol = (() => { try { return node?.volume ?? 1; } catch (_) { return 1; } })();
      if (src && tryPlayWebAudioSfx(src, vol)) return;

      if (restart) {
        try { node.currentTime = 0; } catch (_) {}
      }
      node.play().catch(() => {});
    } catch (_) {}
  }, [canPlaySfx, tryPlayWebAudioSfx]);

  // 初回ユーザー操作後に、よく鳴るSEだけ先読みして「初回だけ重い」を減らす
  useEffect(() => {
    if (!sfxPlaying) return;
    try {
      // ここはユーザー操作外で動くので、resumeは期待せずデコードだけ走らせる
      const urls = [
        `${PUBLIC_PATH}/sounds/lever.mp3`,
        `${PUBLIC_PATH}/sounds/stop.mp3`,
        `${PUBLIC_PATH}/sounds/attack.mp3`,
        `${PUBLIC_PATH}/sounds/hp_charge.mp3`,
        `${PUBLIC_PATH}/sounds/decide.mp3`,
      ];
      // すぐに重くならないよう少し遅延
      const id = setTimeout(() => {
        urls.forEach(u => preloadWebAudioBuffer(u));
      }, 400);
      return () => clearTimeout(id);
    } catch (_) {}
  }, [sfxPlaying, preloadWebAudioBuffer]);

  // 効果音OFFになった瞬間に、再生中のSE/ループSEを止める
  useEffect(() => {
    if (sfxPlaying) return;
    try {
      const refs = [
        leverSoundRef,
        stopSoundRef,
        coingetLongSoundRef,
        coingetSuperLongSoundRef,
        attackSoundRef,
        threeUnkoSoundRef,
        decideSoundRef,
        battleStartSoundRef,
        returnSoundRef,
        hpChargeSoundRef,
        heartbeatLoopRef,
        youWinSoundRef,
        youLoseSoundRef,
      ];
      refs.forEach(ref => {
        try {
          const node = ref?.current;
          if (!node) return;
          try { if (!node.paused) node.pause(); } catch(_) {}
          try { node.currentTime = 0; } catch(_) {}
        } catch(_) {}
      });
    } catch(_) {}
    try {
      const pools = [leverSoundPoolRef, stopSoundPoolRef];
      pools.forEach(p => {
        const nodes = p?.current?.nodes;
        if (!Array.isArray(nodes)) return;
        nodes.forEach(n => {
          try { if (n && !n.paused) n.pause(); } catch(_) {}
          try { if (n) n.currentTime = 0; } catch(_) {}
        });
      });
    } catch(_) {}
    try {
      const nodes = (typeof window !== 'undefined' && Array.isArray(window.__activeSfxNodes)) ? window.__activeSfxNodes : null;
      if (nodes) {
        nodes.forEach(n => {
          try { if (n && !n.paused) n.pause(); } catch(_) {}
          try { if (n) n.currentTime = 0; } catch(_) {}
        });
      }
    } catch(_) {}
  }, [sfxPlaying]);

  // アンティシペーション時のループSE制御
  const setAnticipation = React.useCallback((active, reelIdx = null) => {
    setAnticipationActive(active);
    setAnticipationReelIndex(active ? reelIdx : null);
    try {
      const node = heartbeatLoopRef.current;
      if (!node) return;
      if (!sfxPlaying || document.body.classList.contains('sfx-off')) {
        try { node.pause(); } catch(_) {}
        try { node.currentTime = 0; } catch(_) {}
        return;
      }
      if (active) {
        try { if (node.paused) node.currentTime = 0; } catch(_){}
        node.play().catch(()=>{});
      } else {
        try { node.pause(); } catch(_){}
      }
    } catch(_) {}
  }, [sfxPlaying]);

  // クリーンアップ：アンマウント時にループSE停止
  useEffect(() => {
    return () => {
      try { heartbeatLoopRef.current && heartbeatLoopRef.current.pause(); } catch(_){}
    };
  }, []);
  // 勝利/敗北時の効果音再生
  useEffect(() => {
    try {
      const bgm = bgmRef.current;
      if (gamePhase === 'win') {
        if (!sfxPlaying || document.body.classList.contains('sfx-off')) return;
        const node = youWinSoundRef.current;
        if (node) {
          try { node.pause(); } catch(_){}
          try { node.currentTime = 0; } catch(_){}
          let prev = null;
          if (bgm) { try { prev = bgm.volume; bgm.volume = Math.max(0, prev * 0.45); } catch(_){} }
          try { node.play().catch(()=>{}); } catch(_){}
          setTimeout(() => { if (bgm && prev != null) { try { bgm.volume = prev; } catch(_){} } }, 2000);
        }
      } else if (gamePhase === 'lose') {
        if (!sfxPlaying || document.body.classList.contains('sfx-off')) return;
        const node = youLoseSoundRef.current;
        if (node) {
          try { node.pause(); } catch(_){}
          try { node.currentTime = 0; } catch(_){}
          let prev = null;
          if (bgm) { try { prev = bgm.volume; bgm.volume = Math.max(0, prev * 0.45); } catch(_){} }
          try { node.play().catch(()=>{}); } catch(_){}
          setTimeout(() => { if (bgm && prev != null) { try { bgm.volume = prev; } catch(_){} } }, 2000);
        }
      }
    } catch(_) {}
  }, [gamePhase, sfxPlaying]);
  
  // ミニメニュー: 外側クリック/ESCで閉じる
  useEffect(() => {
    const onDocClick = (e) => {
      if (!showMiniMenu) return;
      const root = miniMenuRef.current;
      if (!root) return;
      const target = e.target;
      if (root.contains(target)) return; // メニュー内なら閉じない
      setShowMiniMenu(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setShowMiniMenu(false);
    };
    document.addEventListener('click', onDocClick, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showMiniMenu]);

  // スワイプジェスチャーでメニュー開閉（画面右端から左へスワイプでメニュー表示、バトル画面のみ、KWSK表示中・カテゴリ未選択時は無効）
  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;

    const onTouchStart = (e) => {
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartTime = Date.now();
    };

    const onTouchEnd = (e) => {
      // バトル画面以外、KWSK表示中、またはカテゴリ未選択時は反応しない
      if (gamePhase !== 'battle' || showKwsk || !gameCategory) return;

      const touch = e.changedTouches[0];
      const touchEndX = touch.clientX;
      const touchEndY = touch.clientY;
      const touchEndTime = Date.now();

      const deltaX = touchEndX - touchStartX;
      const deltaY = touchEndY - touchStartY;
      const deltaTime = touchEndTime - touchStartTime;

      // スワイプ判定条件（スマホで開きやすいように調整）
      const minSwipeDistance = 30; // 30pxスワイプで開く（より短く）
      const maxSwipeTime = 500; // 500ms以内（より長く）
      const minStartX = window.innerWidth - 100; // 画面右端100px以内からスタート（より広く）

      // 右端から左へのスワイプでメニューを開く
      if (
        touchStartX >= minStartX &&
        deltaX < -minSwipeDistance &&
        Math.abs(deltaY) < Math.abs(deltaX) * 0.8 // 斜めスワイプも許容
        && deltaTime < maxSwipeTime
      ) {
        setShowMiniMenu(true);
      }

      // メニューが開いている時、左から右へのスワイプで閉じる
      if (
        showMiniMenu &&
        deltaX > minSwipeDistance &&
        Math.abs(deltaY) < Math.abs(deltaX) * 0.8 &&
        deltaTime < maxSwipeTime
      ) {
        setShowMiniMenu(false);
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [showMiniMenu, gamePhase, showKwsk, gameCategory]);

  // PCキーボード操作（バトル中のみ有効）
  useEffect(() => {
    const isDesktop = typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!isDesktop) return;

    const onKeyDown = (e) => {
      // 入力欄やテキストエリアにフォーカスがある場合は無視
      const tag = e.target && e.target.tagName ? e.target.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea') return;

      if (gamePhase !== 'battle') return;

      // KWSK表示中は ESC/Backspace で閉じるのみ受け付け、他のショートカットは無効化
      if (showKwsk) {
        if (e.key === 'Escape' || e.key === 'Backspace') {
          e.preventDefault();
          // setShowKwsk(false); setKwskEntry(null); // 発光はSTART/画面遷移以外で消さない
        }
        return;
      }

      // ミニメニュー表示中は上下矢印などをメニュー操作に割り当て
      if (showMiniMenu && miniMenuRef.current) {
        const items = Array.from(miniMenuRef.current.querySelectorAll('.mini-menu-item'));
        const focusAt = (idx) => {
          if (!items.length) return;
          const next = (idx + items.length) % items.length;
          items.forEach(el => el.classList.remove('kb-focus'));
          const el = items[next];
          try { el.classList.add('kb-focus'); el.focus({ preventScroll: true }); } catch(_) {}
          setMiniMenuKbIndex(next);
        };
        if (e.key === 'ArrowDown') { e.preventDefault(); focusAt(miniMenuKbIndex + 1); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); focusAt(miniMenuKbIndex - 1); return; }
        if (e.key === 'Home') { e.preventDefault(); focusAt(0); return; }
        if (e.key === 'End') { e.preventDefault(); focusAt(items.length - 1); return; }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const el = items[miniMenuKbIndex];
          try { el?.click(); } catch(_) {}
          return;
        }
        if (e.key === 'Escape') { e.preventDefault(); setShowMiniMenu(false); return; }
        // メニュー表示中はここで処理を終える
      }

      // Space / ArrowUp: スタート（全停止時のみ）
      const isSpaceKey = e.code === 'Space' || e.key === ' ' || e.key === 'Space' || e.key === 'Spacebar';
      const isArrowUp = e.key === 'ArrowUp';
      if (isSpaceKey || isArrowUp) {
        e.preventDefault(); // ページスクロール抑止
        if (e.repeat) return; // 長押しリピートでの多重実行を防止
        // 放置モード中はスタート操作を受け付けない（自動で進行）
        if (playMode === 'auto' && gamePhase === 'battle') return;
        try {
          const allStopped = Array.isArray(spinning) ? spinning.every((s) => !s) : false;
          if (allStopped) {
            startSpinWithMondaiSwitch();
          }
        } catch(_) {}
        return;
      }

      // 1/Q: 左リール停止、2/W: 中リール停止、3/E: 右リール停止
      const stopKeysMap = {
        '1': 0, 'q': 0, 'Q': 0,
        '2': 1, 'w': 1, 'W': 1,
        '3': 2, 'e': 2, 'E': 2,
      };
      if (stopKeysMap[e.key] != null) {
        e.preventDefault();
        // 放置モード中は停止操作を受け付けない（自動で進行）
        if (playMode === 'auto' && gamePhase === 'battle') return;
        const idx = stopKeysMap[e.key];
        try { stopReel(idx); } catch(_) {}
        return;
      }

      // ArrowLeft / ArrowRight: 最初/最後に回っているリールを停止
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        // 放置モード中は停止操作を受け付けない（自動で進行）
        if (playMode === 'auto' && gamePhase === 'battle') return;
        try {
          const spinningIdxs = Array.isArray(spinning) ? spinning.map((s,i)=> s? i : null).filter(i=> i!==null) : [];
          if (spinningIdxs.length > 0) {
            const targetIdx = e.key === 'ArrowLeft' ? spinningIdxs[0] : spinningIdxs[spinningIdxs.length - 1];
            stopReel(targetIdx);
          }
        } catch(_) {}
        return;
      }

      // Enter / ArrowDown: 詳しく（KWSK）を開く（正解時のみ）
      if ((e.key === 'Enter' || e.key === 'ArrowDown') && highlightIsCorrect === true) {
        e.preventDefault();
        try { openKwskDetails(); } catch(_) {}
        return;
      }
      // M: メニュー表示トグル（BGMトグルは B キーへ移行）
      if (e.key === 'm' || e.key === 'M') {
        e.preventDefault();
        try { setShowMiniMenu(v => !v); } catch(_) {}
        return;
      }
      // B: BGMトグル
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        try {
          const bgm = bgmRef.current;
          if (bgm) { if (bgm.paused) { bgm.play().catch(()=>{}); } else { bgm.pause(); } }
        } catch(_) {}
        return;
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('keydown', onKeyDown); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamePhase, spinning, shouldSwitchMondai, currentMondai, highlightIsCorrect, showMiniMenu, miniMenuKbIndex, showKwsk, playMode]);

  // KWSKオーバーレイ表示中: ESC/Backspaceで閉じる + 矢印/PageUp/PageDown/Home/Endで縦スクロール（全フェーズ共通）
  useEffect(() => {
    if (!showKwsk) return;
    const onKey = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      // 閉じる操作
      if (e.key === 'Escape' || e.key === 'Backspace') {
        e.preventDefault();
        setShowKwsk(false); setKwskEntry(null);
        return;
      }
      // スクロール操作
      const el = kwskCardRef.current;
      if (el) {
        const lineStep = Math.max(24, Math.floor(el.clientHeight * 0.15)); // 行感覚のステップ
        const pageStep = Math.max(80, Math.floor(el.clientHeight * 0.9));
        if (e.key === 'ArrowDown') { e.preventDefault(); el.scrollTop = Math.min(el.scrollTop + lineStep, el.scrollHeight); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); el.scrollTop = Math.max(el.scrollTop - lineStep, 0); return; }
        if (e.key === 'PageDown') { e.preventDefault(); el.scrollTop = Math.min(el.scrollTop + pageStep, el.scrollHeight); return; }
        if (e.key === 'PageUp') { e.preventDefault(); el.scrollTop = Math.max(el.scrollTop - pageStep, 0); return; }
        if (e.key === 'Home') { e.preventDefault(); el.scrollTop = 0; return; }
        if (e.key === 'End') { e.preventDefault(); el.scrollTop = el.scrollHeight; return; }
      }
      // その他のキーはゲーム操作を防ぐため抑止
      e.preventDefault();
    };
    // capture=true で他のハンドラより先に制御
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [showKwsk]);

  // PC向け：スロット画面の外枠ホバーでメニュー表示（バトル画面のみ、KWSK表示中・カテゴリ未選択時は無効）
  useEffect(() => {
    let hoverTimeout = null;

    const onMouseMove = (e) => {
      // バトル画面以外、KWSK表示中、またはカテゴリ未選択時は反応しない
      if (gamePhase !== 'battle' || showKwsk || !gameCategory) {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }
        return;
      }

      // スロット画面のコンテナ（maxWidth: 400px, margin: 0 auto）
      const containerMaxWidth = 400;
      const viewportWidth = window.innerWidth;
      
      // コンテナの左右位置を計算
      const containerLeft = viewportWidth > containerMaxWidth 
        ? (viewportWidth - containerMaxWidth) / 2 
        : 0;
      const containerRight = viewportWidth > containerMaxWidth 
        ? containerLeft + containerMaxWidth 
        : viewportWidth;
      
      const edgeThreshold = 20; // 外枠20px以内
      
      // スロット画面の左右外側にマウスがあるか判定
      const isInLeftEdge = e.clientX >= containerLeft - edgeThreshold && e.clientX <= containerLeft;
      const isInRightEdge = e.clientX >= containerRight && e.clientX <= containerRight + edgeThreshold;
      const isNearEdge = isInLeftEdge || isInRightEdge;

      if (isNearEdge && !showMiniMenu) {
        // ホバーして200ms後にメニューを開く（誤操作防止）
        if (!hoverTimeout) {
          hoverTimeout = setTimeout(() => {
            setShowMiniMenu(true);
          }, 200);
        }
      } else if (!isNearEdge && !showMiniMenu) {
        // メニューが開いていない時のみタイマーをクリア
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }
      }
    };

    // デスクトップのみ有効化（タッチデバイスでは無効）
    const isDesktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    
    if (isDesktop) {
      document.addEventListener('mousemove', onMouseMove, { passive: true });
    }

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [showMiniMenu, gamePhase, showKwsk, gameCategory]);

  // メニュー外クリックでメニューを閉じる
  useEffect(() => {
    if (!showMiniMenu) return;

    const handleClickOutside = (e) => {
      // メニュー要素を取得
      if (miniMenuRef.current && !miniMenuRef.current.contains(e.target)) {
        setShowMiniMenu(false);
      }
    };

    // クリックイベントを監視
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMiniMenu]);

  // ミニメニュー表示時に最初の項目へキーボードフォーカスを当てる
  useEffect(() => {
    if (!showMiniMenu || !miniMenuRef.current) return;
    try {
      const items = Array.from(miniMenuRef.current.querySelectorAll('.mini-menu-item'));
      if (items.length) {
        items.forEach(el => el.classList.remove('kb-focus'));
        items[0].classList.add('kb-focus');
        items[0].focus({ preventScroll: true });
        setMiniMenuKbIndex(0);
      }
    } catch(_) {}
  }, [showMiniMenu]);

  // 統計データをlocalStorageに保存
  useEffect(() => {
    try {
      localStorage.setItem('slotStats', JSON.stringify(stats));
    } catch (e) {
      console.error('Failed to save stats:', e);
    }
  }, [stats]);

  // 勝敗の記録（カテゴリ別）
  useEffect(() => {
    if (!gameCategory || !currentEnemy) return;
    
    if (gamePhase === 'win') {
      setStats(prev => {
        const newWinStreak = prev.currentWinStreak + 1;
        const enemyName = currentEnemy.displayName;
        const categoryData = prev.byCategory[gameCategory] || {};
        const enemyStats = categoryData[enemyName] || {
          totalSpins: 0, totalWins: 0, totalLosses: 0, bigWins: 0,
          maxConsecutiveWins: 0, maxConsecutiveLosses: 0, poopExplosions: 0, commandCounts: {}
        };
        const newMaxWins = Math.max(enemyStats.maxConsecutiveWins, newWinStreak);
        
        return {
          ...prev,
          byCategory: {
            ...prev.byCategory,
            [gameCategory]: {
              ...categoryData,
              [enemyName]: {
                ...enemyStats,
                totalWins: enemyStats.totalWins + 1,
                maxConsecutiveWins: newMaxWins
              }
            }
          },
          currentWinStreak: newWinStreak,
          currentLoseStreak: 0
        };
      });
    } else if (gamePhase === 'lose') {
      setStats(prev => {
        const newLoseStreak = prev.currentLoseStreak + 1;
        const enemyName = currentEnemy.displayName;
        const categoryData = prev.byCategory[gameCategory] || {};
        const enemyStats = categoryData[enemyName] || {
          totalSpins: 0, totalWins: 0, totalLosses: 0, bigWins: 0,
          maxConsecutiveWins: 0, maxConsecutiveLosses: 0, poopExplosions: 0, commandCounts: {}
        };
        const newMaxLosses = Math.max(enemyStats.maxConsecutiveLosses, newLoseStreak);
        
        return {
          ...prev,
          byCategory: {
            ...prev.byCategory,
            [gameCategory]: {
              ...categoryData,
              [enemyName]: {
                ...enemyStats,
                totalLosses: enemyStats.totalLosses + 1,
                maxConsecutiveLosses: newMaxLosses
              }
            }
          },
          currentLoseStreak: newLoseStreak,
          currentWinStreak: 0
        };
      });
    }
  }, [gamePhase, gameCategory, currentEnemy]);

  // ワンショットSFX再生（clone + リトライ + BGMダッキング）
  const playOneShot = React.useCallback((audioRef, { volume, duckBgm = true } = {}) => {
    try {
      const canPlayNow = () => {
        try { return sfxPlaying && !document.body.classList.contains('sfx-off'); } catch (_) { return !!sfxPlaying; }
      };
      if (!canPlayNow()) return;
      const base = audioRef?.current;
      if (!base || !base.src) return;
      const node = base.cloneNode(true);
      try { if (typeof volume === 'number') node.volume = volume; else node.volume = base.volume ?? 1; } catch(_) {}

      // 効果音OFFに切り替えたら止められるよう、再生中ノードを追跡
      try {
        if (typeof window !== 'undefined') {
          if (!Array.isArray(window.__activeSfxNodes)) window.__activeSfxNodes = [];
          window.__activeSfxNodes.push(node);
        }
      } catch(_) {}

      // BGMダッキング
      let restored = false;
      let prevBgmVol = null;
      const bgm = bgmRef.current;
      if (duckBgm && bgm) {
        try {
          prevBgmVol = bgm.volume;
          bgm.volume = Math.max(0, prevBgmVol * 0.45);
        } catch(_) {}
      }
      const restore = () => {
        if (restored) return; restored = true;
        if (duckBgm && bgm && prevBgmVol != null) {
          try { bgm.volume = prevBgmVol; } catch(_) {}
        }
        try {
          const list = (typeof window !== 'undefined' && Array.isArray(window.__activeSfxNodes)) ? window.__activeSfxNodes : null;
          if (list) {
            const idx = list.indexOf(node);
            if (idx >= 0) list.splice(idx, 1);
          }
        } catch(_) {}
      };
      node.addEventListener('ended', restore, { once: true });
      const tryPlay = () => {
        try {
          if (!canPlayNow()) return;
          const p = node.play();
          if (p && typeof p.then === 'function') {
            p.then(() => {}).catch(() => setTimeout(() => {
              try {
                if (!canPlayNow()) return;
                node.play().catch(() => {});
              } catch(_) {}
            }, 120));
          }
        } catch(_) {
          setTimeout(() => {
            try {
              if (!canPlayNow()) return;
              node.play().catch(() => {});
            } catch(_) {}
          }, 120);
        }
      };
      tryPlay();
      // 念のためタイムアウトでもBGM復帰
      setTimeout(restore, 4000);
    } catch(_) {}
  }, [sfxPlaying]);

  // 固定オフセット使用のため、幅計測による再配置は行わない

  // 新しい相手との対戦開始時にリールを再読み込みし、各種表示状態をリセット
  const resetForNewOpponent = React.useCallback(() => {
    try { setReelSymbols(generateReelSymbols(yojijukugoList)); } catch(_) {}
    setReelResetNonce(n => n + 1);
    // setHighlightIndexes([]); // START/画面遷移以外で消さない
    setMatchedText("");
    setTargetIndexes([null, null, null]);
    setSpinning([false, false, false]);
    setPayout(0);
    setHasSpun(false);
    setSuppressBorderGlow(false);
    setPredeterminedResults([null, null, null]);
    setPoopCount(0);
    setPatiencePoints(0);
    setGarlicBlinking(false);
    setPoopBlinking(false);
    // setShowKwsk(false);
    // setKwskEntry(null);
  try { if (typeof window !== 'undefined') { window.yojijukugoList = yojijukugoList; } } catch(_) {}
  }, [yojijukugoList]);

  // グローバルメニューからのBGM制御（body class: bgm-off）を監視し、bgmPlayingと同期
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isBgmOff = document.body.classList.contains('bgm-off');
      setBgmPlaying(!isBgmOff);
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // グローバルメニューからの効果音制御（body class: sfx-off）を監視し、sfxPlayingと同期
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isSfxOff = document.body.classList.contains('sfx-off');
      setSfxPlaying(!isSfxOff);
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // モバイルは user gesture 外の play() がブロックされやすい。
  // ヘッダーのトグル操作中に直接呼べる即時制御関数を提供する。
  useEffect(() => {
    try {
      window.__setBgmEnabledImmediate = (enabled) => {
        try { document.body.classList.toggle('bgm-off', !enabled); } catch (_) {}
        try { localStorage.setItem('bgmEnabled', enabled ? '1' : '0'); } catch (_) {}
        try { window.bgmEnabled = enabled; } catch (_) {}
        try { setBgmPlaying(!!enabled); } catch (_) {}

        const bgm = bgmRef.current;
        if (!bgm) return;
        if (!enabled) {
          try { if (!bgm.paused) bgm.pause(); } catch (_) {}
          return;
        }
        // 仕様: START押下まで自動再生しない。
        // ただし、すでにゲーム操作済み(= hasSpun)なら、トグルONで再開を試みる。
        try {
          const allowResume = !!hasSpunRef.current || gamePhaseRef.current === 'battle';
          if (allowResume && bgm.paused) {
            bgm.play().catch(() => {});
          }
        } catch (_) {}
      };
    } catch (_) {}
    return () => {
      try { if (window.__setBgmEnabledImmediate) delete window.__setBgmEnabledImmediate; } catch (_) {}
    };
  }, []);

  // ヘッダーから放置速度を即時反映できるようにする
  useEffect(() => {
    try {
      window.__setAutoPlaySpeedImmediate = (speed) => {
        const v = (speed === 'slow' || speed === 'fast' || speed === 'normal') ? speed : 'normal';
        try { localStorage.setItem('autoPlaySpeed', v); } catch (_) {}
        try { window.autoPlaySpeed = v; } catch (_) {}
        try { setAutoPlaySpeed(v); } catch (_) {}
      };
    } catch (_) {}
    return () => {
      try { if (window.__setAutoPlaySpeedImmediate) delete window.__setAutoPlaySpeedImmediate; } catch (_) {}
    };
  }, []);

  useEffect(() => {
    // BGMインスタンスを一度だけ作成。
    if (!bgmRef.current) {
      const bgmCandidates = [
        `${PUBLIC_PATH}/sounds/Shooting_01.mp3`,
      ];
      const pickedSrc = bgmCandidates[Math.floor(Math.random() * bgmCandidates.length)];
      const bgm = new Audio();
      bgm.preload = 'none';
      bgm.src = pickedSrc;
      bgm.loop = true;
      bgm.volume = 0.2;
      bgmRef.current = bgm;
    }
    return () => {
      const bgm = bgmRef.current;
      if (!bgm) return;
      try { if (!bgm.paused) bgm.pause(); } catch(_) {}
      try { bgm.currentTime = 0; } catch(_) {}
    };
  }, []);

  // BGM ON/OFF を反映（ONに戻したら可能なら再開）
  useEffect(() => {
    const bgm = bgmRef.current;
    if (!bgm) return;
    if (!bgmPlaying) {
      try { if (!bgm.paused) bgm.pause(); } catch(_) {}
      return;
    }
    // ONでも常に自動再生はしないが、すでに一度ゲーム操作が行われているなら再開を試みる
    try {
      const allowResume = hasSpun || gamePhase === 'battle';
      if (allowResume && bgm.paused) {
        bgm.play().catch(() => {});
      }
    } catch(_) {}
  }, [bgmPlaying, hasSpun, gamePhase]);

  // バトル開始ごとにBGMをランダムで選び直す（再生はSTART押下時に実行）
  useEffect(() => {
    if (gamePhase === 'battle') {
      const candidates = [
        `${PUBLIC_PATH}/sounds/Shooting_01.mp3`,
      ];
      const picked = candidates[Math.floor(Math.random() * candidates.length)];
      try {
        const bgm = bgmRef.current ?? new Audio();
        try { bgm.preload = 'none'; } catch (_) {}
        try { bgm.pause(); } catch (_) {}
        // 同じsrcを毎回代入すると、再取得が走ってCloudFrontログが増えやすい。
        // 可能なら src の再設定を避ける。
        let pickedUrl = picked;
        try { pickedUrl = new URL(picked, window.location.href).href; } catch (_) {}
        const currentUrl = (bgm.currentSrc || bgm.src || '');
        if (currentUrl !== pickedUrl) {
          bgm.src = picked;
        }
        bgm.loop = true;
        bgm.volume = 0.2;
        try { bgm.currentTime = 0; } catch (_) {}
        bgmRef.current = bgm;
        // デバッグ用に選曲をコンソール出力
        try { console.log(`[BGM] picked: ${picked}`); } catch(_) {}
      } catch (_) {}
    }
  }, [gamePhase, playSfxNode]);

  // 全てのリールが停止したかを判定
  const allReelsStopped = spinning.every((s) => !s);

  // 5回ミスで強制ダメージ（トップレベルに移動）
  // 問題切り替え時にミスカウントをリセット
  useEffect(() => {
    setConsecutiveLosses(0);
  }, [currentMondai]);

  // 1問につき3回揃い判定が不正解だった場合はHPダメージせず、問題切替のみ（ダメージはうんこ3個時のみ）
  useEffect(() => {
    if (consecutiveLosses === 3) {
      // HPダメージは与えず、問題のみ切り替え
      setShouldSwitchMondai(true);
      // カウンタをリセット
      setConsecutiveLosses(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consecutiveLosses, getMondaiFromCommands]);
  useEffect(() => {
    if (allReelsStopped && hasSpun) {
      // リールが回転した後に揃い判定を行う
      let localMatchedText = ""; // ローカル変数で判定
      // 正解/不正解の判定結果（このスピンに限定した同期フラグ）
      let wasCorrectMatch = false;
      const checkPayout = () => {
        const visibleSymbols = reelSymbols.map((symbols, reelIndex) => {
          const targetIndex = targetIndexes[reelIndex];
          const length = symbols.length;
          // UI 表示基準: 中段 = targetIndex
          return [
            symbols[(targetIndex - 1 + length) % length], // 上段
            symbols[targetIndex % length],                // 中段 (ペイライン)
            symbols[(targetIndex + 1) % length],          // 下段
          ];
        });

        // === まとめ可視絵柄ログ ===
        try {
          // console.groupCollapsed('全リール停止: 可視3x3絵柄マトリクス');
          // 列ごとにログ（リール）
            visibleSymbols.forEach((reel, rIdx) => {
              // ログ出力停止（可視絵柄の詳細ログは不要）
              // console.log(`リール${rIdx + 1}: 上=${reel[0]?.text} / 中=${reel[1]?.text} / 下=${reel[2]?.text}`);
            });
          // 行方向（ペイライン視点）
          // ログ停止に伴い未使用の行データ生成も削除
          // const topRow = visibleSymbols.map(r => r[0]?.text).join(' - ');
          // const midRow = visibleSymbols.map(r => r[1]?.text).join(' - ');
          // const bottomRow = visibleSymbols.map(r => r[2]?.text).join(' - ');
          // ログ出力停止（上/中/下段ラインの可視絵柄）
          // console.log('上段ライン:', topRow);
          // console.log('中段ライン:', midRow);
          // console.log('下段ライン:', bottomRow);
          console.groupEnd();
        } catch (e) {
          // console.warn('全停止時可視絵柄ログ出力中に例外:', e);
        }

        // 見えている絵柄をコンソールに表示
        // console.log("見えている絵柄:");
        // visibleSymbols.forEach((reel, index) => {
        //   console.log(`リール${index + 1}:`, reel.map((symbol) => symbol.text));
        // });

        // 揃いの判定
        const isMatchRow = (row) =>
          visibleSymbols.every(col => col[row]?.text === visibleSymbols[0][row]?.text);

        const horizontalMatchTop = isMatchRow(0);    // 上段
        const horizontalMatchMiddle = isMatchRow(1); // 中段 (ペイライン)
        const horizontalMatchBottom = isMatchRow(2); // 下段

        // 斜め揃いの判定
        const diagonalMatch1 =
          visibleSymbols[0][0]?.text === visibleSymbols[1][1]?.text &&
          visibleSymbols[1][1]?.text === visibleSymbols[2][2]?.text;

        const diagonalMatch2 =
          visibleSymbols[0][2]?.text === visibleSymbols[1][1]?.text &&
          visibleSymbols[1][1]?.text === visibleSymbols[2][0]?.text;

        if (horizontalMatchTop || horizontalMatchMiddle || horizontalMatchBottom || diagonalMatch1 || diagonalMatch2) {
          const newHighlightIndexes = [null, null, null];
          if (horizontalMatchTop) {
            localMatchedText = visibleSymbols[0][0]?.text;
            newHighlightIndexes[0] = (targetIndexes[0] - 1 + reelSymbols[0].length) % reelSymbols[0].length;
            newHighlightIndexes[1] = (targetIndexes[1] - 1 + reelSymbols[1].length) % reelSymbols[1].length;
            newHighlightIndexes[2] = (targetIndexes[2] - 1 + reelSymbols[2].length) % reelSymbols[2].length;
          }
          if (horizontalMatchMiddle) {
            localMatchedText = visibleSymbols[0][1]?.text;
            newHighlightIndexes[0] = targetIndexes[0];
            newHighlightIndexes[1] = targetIndexes[1];
            newHighlightIndexes[2] = targetIndexes[2];
          }
          if (horizontalMatchBottom) {
            localMatchedText = visibleSymbols[0][2]?.text;
            newHighlightIndexes[0] = (targetIndexes[0] + 1) % reelSymbols[0].length;
            newHighlightIndexes[1] = (targetIndexes[1] + 1) % reelSymbols[1].length;
            newHighlightIndexes[2] = (targetIndexes[2] + 1) % reelSymbols[2].length;
          }
          if (diagonalMatch1) { // 左上→右下: 上,中,下
            localMatchedText = visibleSymbols[0][0]?.text;
            newHighlightIndexes[0] = (targetIndexes[0] - 1 + reelSymbols[0].length) % reelSymbols[0].length; // 上
            newHighlightIndexes[1] = targetIndexes[1]; // 中
            newHighlightIndexes[2] = (targetIndexes[2] + 1) % reelSymbols[2].length; // 下
          }
          if (diagonalMatch2) { // 左下→右上: 下,中,上
            localMatchedText = visibleSymbols[0][2]?.text;
            newHighlightIndexes[0] = (targetIndexes[0] + 1) % reelSymbols[0].length; // 下
            newHighlightIndexes[1] = targetIndexes[1]; // 中
            newHighlightIndexes[2] = (targetIndexes[2] - 1 + reelSymbols[2].length) % reelSymbols[2].length; // 上
          }
          setHighlightIndexes(newHighlightIndexes);
          setMatchedText(localMatchedText || "");
          // === 揃い時に自動攻撃 ===
          // 正解コマンドを取得
          const correctCommand = (() => {
            const found = yojijukugoList.find(item => item.mondai === currentMondai);
            return found ? found.text : null;
          })();
          // matchedTextは揃い時のみセットされる
          if (localMatchedText && correctCommand) {
            if (localMatchedText === correctCommand) {
              setHighlightIsCorrect(true); // 正解ハイライト（色分け）
              wasCorrectMatch = true;
              // 放置モード時は、正解成立後に2秒だけ自動進行を止める
              try {
                if (playMode === 'auto') {
                  const until = Date.now() + 2000;
                  autoPlayPauseUntilRef.current = Math.max(autoPlayPauseUntilRef.current || 0, until);
                }
              } catch (_) {}
              // 正解時のみうんこマークカウントをリセット
              setPoopCount(0);
              // 攻撃アニメーションタイプをランダムに選択
              const animTypes = ['lightning', 'fire', 'slash', 'impact'];
              const randomType = animTypes[Math.floor(Math.random() * animTypes.length)];
              setAttackAnimType(randomType);
              setEnemyAttackAnim(true);
              // 正解コマンドを攻撃表示用にセット
              setAttackingCommand(localMatchedText);
              // 攻撃SEを再生
              try {
                if (attackSoundRef.current) {
                  playSfxNode(attackSoundRef.current);
                }
              } catch (err) {
                console.log('Attack sound error:', err);
              }
              const playerDamage = Math.round(30 * (currentEnemy?.damageTakenMultiplier ?? 1.0)); // 調整: 20 → 30
              const newHP = Math.max(0, enemyHP - playerDamage);
              setEnemyHP(newHP);
              // HPアニメーション開始
              animateHP(enemyDisplayHP, newHP, setEnemyDisplayHP);
              setTimeout(() => {
                setEnemyAttackAnim(false);
                // 攻撃コマンド表示を1500ms後にクリア
                setTimeout(() => setAttackingCommand(null), 1500);
              }, 300);
              setConsecutiveLosses(0); // 正解時はミスカウントリセット
              setPatiencePoints(0); // 正解時はにんにくポイントもリセット
              setShouldSwitchMondai(true); // 正解成立時にフラグON
              setIsCorrectAnswer(true); // 正解時のみ発光フラグをON
              setEuphoriaActive(true); // 次のスタートまで枠発光を持続
              // 直前の正解コマンドを保存（結果画面からKWSKを開けるため）
              try {
                const entry = yojijukugoList.find(it => it.text === localMatchedText);
                setLastCorrectEntry(entry || null);
              } catch(_) { setLastCorrectEntry(null); }
            } else {
              setHighlightIsCorrect(false); // 不正解ハイライト（色分け）
              // 不正解だが絵柄が揃っている場合、にんにくポイントを1つ獲得
              setPatiencePoints(prevPoints => {
                const newPoints = prevPoints + 1;
                if (newPoints >= 3) {
                  // 3つ貯まったらにんにくマークを点滅させてからHP回復
                  setGarlicBlinking(true);
                  setTimeout(() => {
                    // HP回復音を再生
                    try { if (hpChargeSoundRef.current) { playSfxNode(hpChargeSoundRef.current); } } catch(_) {}
                    const healedHP = Math.min(100, playerHP + 15);
                    setPlayerHP(healedHP);
                    animateHP(playerDisplayHP, healedHP, setPlayerDisplayHP);
                    setGarlicBlinking(false);
                  }, 300); // 0.3秒間点滅してからHP回復
                  return 0; // ポイントリセット
                }
                return newPoints;
              });
              // 3回目の不正解でのみダメージ
              setConsecutiveLosses(losses => Math.min(3, losses + 1));
            }
          }
          // 揃い時の払い出し: 正解=7 / 不正解=1
          return (localMatchedText && correctCommand && localMatchedText === correctCommand) ? 7 : 4;
        }

        setHighlightIndexes([]); // 揃っていない場合はリセット
  // setHighlightIsCorrect(null); // START/画面遷移以外で消さない
        
        // 何も揃わなかった場合、うんこマークカウントを増やす
        setPoopCount(prevCount => {
          const newCount = prevCount + 1;
          if (newCount >= 3) {
            // 3回連続で何も揃わなかった場合、うんこマークを点滅させてからダメージ
              setPoopBlinking(true);
            setTimeout(() => {
              // 爆発エフェクト開始
              setPoopExploding(true);
              setPlayerAttackAnim(true);
              // うんこ爆発カウント増加
              setStats(prev => {
                const updated = { ...prev };
                const enemyName = currentEnemy?.displayName || 'unknown';
                if (updated.byCategory[gameCategory]) {
                  if (!updated.byCategory[gameCategory][enemyName]) {
                    updated.byCategory[gameCategory][enemyName] = {
                      totalSpins: 0, totalWins: 0, totalLosses: 0, bigWins: 0,
                      maxConsecutiveWins: 0, maxConsecutiveLosses: 0, poopExplosions: 0, commandCounts: {}
                    };
                  }
                  updated.byCategory[gameCategory][enemyName].poopExplosions += 1;
                }
                return updated;
              });
              // うんこ3個ダメージSE再生（安定化再生）
              playOneShot(threeUnkoSoundRef, { volume: 0.7, duckBgm: true });
              const enemyAttack = Math.round(currentEnemy?.attackPower ?? 15);
              const newHP = Math.max(0, playerHP - enemyAttack);
              setPlayerHP(newHP);
              animateHP(playerDisplayHP, newHP, setPlayerDisplayHP);
              setTimeout(() => {
                setPlayerAttackAnim(false);
                setPoopExploding(false);
              }, 300); // 爆発演出の長さ（0.3秒）
              setPoopBlinking(false);
              // ダメージ適用後にカウントを0へリセット
              setPoopCount(0);
            }, 300); // 0.3秒間点滅してからダメージ
            return 3; // ダメージ適用までは3個のまま表示
          }
          return newCount;
        });
        
        return 0;
      };

      const payoutAmount = checkPayout();
      setPayout(payoutAmount);
      
      // カウントアップアニメーション
      if (payoutAmount > 0) {
        // バトルメッセージ（配当に応じた簡易文）
        setIsCountingUp(true); // カウントアップ開始







        // カウンターの初期化
        payoutCounterRef.current = {
          counting: true,
          current: 0,
          target: payoutAmount
        };
        // コイン払い出しサウンド（許可）
        let payoutSound = null;
        // 正解:7枚 → coinget_long、(現状11枚は未使用)
        if (payoutAmount === 7) {
          payoutSound = coingetLongSoundRef.current;
        } else if (payoutAmount === 11) {
          payoutSound = coingetSuperLongSoundRef.current;
        }
        // 不正解そろいでも同じ音を鳴らす（payout>0 かつ 不正解）
        if (!payoutSound && payoutAmount > 0 && !isCorrectAnswer) {
          payoutSound = coingetLongSoundRef.current;
        }
        if (payoutSound) {
          // 再生前に一度リセット
          try { payoutSound.pause(); } catch (e) {}
          try { payoutSound.currentTime = 0; } catch (e) {}
          try { payoutSound.loop = true; } catch (e) {}
          if (!document.body.classList.contains('sfx-off')) {
            try {
              payoutSound.play().catch(error => {
                if (error?.name !== "AbortError") {
                  console.error("コイン音再生エラー:", error);
                }
              });
            } catch (e) {
              if (e?.name !== "AbortError") console.error("コイン音再生例外:", e);
            }
          }
        }
        // 1枚ずつカウントアップするアニメーション
        const countUp = () => {
          if (payoutCounterRef.current.current < payoutCounterRef.current.target) {
            setCoins(prev => prev + 1);
            payoutCounterRef.current.current++;
            setTimeout(countUp, 100);
          } else {
            payoutCounterRef.current.counting = false;
            setIsCountingUp(false); // カウントアップ終了
            // 不正解そろいで抑制していた外枠発光を解除
            setSuppressBorderGlow(false);
            if (payoutSound) {
              try { payoutSound.pause(); } catch (e) {}
              try { payoutSound.currentTime = 0; } catch (e) {}
            }
          }
        };
        countUp();
      }
      
      // setBet(false); // BETボタン廃止により不要
      setHasSpun(false); // スピン状態をリセットして無限ループを防止

      // console.log(`今回の払い出し: ${payoutAmount}枚`);

      if (payoutAmount > 0) {
        setConsecutiveLosses(0);
        // 正解時のみアニメーション表示を開始（不正解そろいは絵柄発光のみ）
        if (wasCorrectMatch) {
          setShowAnimation(true);
        } else {
          // 不正解そろい：周囲発光を抑制（コインカウント完了まで継続）、リール絵柄だけ発光
          setSuppressBorderGlow(true);
          // 一定時間後に絵柄のハイライトのみ解除（外枠抑制はカウント終了時に解除）
          setTimeout(() => {
            // setHighlightIndexes([]);
            // setHighlightIsCorrect(null);
          }, 1600);
        }

  // matchedTextをクロージャで直接渡す
  // ターミナル演出撤去
        
        // 既存タイマーをクリア（複数回トリガー時の競合防止）
        // フェーズ移行タイマーは撤去済み
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
          hideTimerRef.current = null;
        }
        if (reelHitAudioRef.current) {
          try {
            reelHitAudioRef.current.pause();
            reelHitAudioRef.current.currentTime = 0;
          } catch (e) {}
          reelHitAudioRef.current = null;
        }

        // 不正解そろい専用のコマンド別ヒット音は使用しない（正解と同じ音で統一）

        if (wasCorrectMatch) {
          // 正解時はアニメーションのみ終了（発光はSTARTまたは画面遷移まで持続）
          hideTimerRef.current = setTimeout(() => {
            setShowAnimation(false);
          }, PHASE1_DURATION_MS + PHASE2_ADDITIONAL_MS);
        }
        
        // 不正解そろいの時は、リール絵柄の発光を一定時間後に消す
        if (!wasCorrectMatch) {
          setTimeout(() => {
            // setHighlightIndexes([]);
            // setHighlightIsCorrect(null);
          }, 1600); // もう少し長めに点滅させてから消す
        }
      } else {
        const newLosses = consecutiveLosses + 1;
        setConsecutiveLosses(newLosses);
        // 外れ時のメッセージ
      }
      
      // 統計データを更新（スピン回数と3揃いをカテゴリ→敵名別にカウント）
      if (gameCategory && currentEnemy) {
        setStats(prev => {
          const enemyName = currentEnemy.displayName;
          const categoryData = prev.byCategory[gameCategory] || {};
          const enemyStats = categoryData[enemyName] || {
            totalSpins: 0, totalWins: 0, totalLosses: 0, bigWins: 0,
            maxConsecutiveWins: 0, maxConsecutiveLosses: 0, poopExplosions: 0, commandCounts: {}
          };
          const isBigWin = highlightIndexes.length === 3;
          
          // コマンドごとのカウントを更新
          const updatedCommandCounts = { ...enemyStats.commandCounts };
          
          // 停止した各リールのコマンドをカウント
          for (let i = 0; i < 3; i++) {
            if (reelSymbols[i] && reelSymbols[i][targetIndexes[i]]) {
              const command = reelSymbols[i][targetIndexes[i]].text;
              if (command) {
                updatedCommandCounts[command] = (updatedCommandCounts[command] || 0) + 1;
              }
            }
          }
          
          return {
            ...prev,
            byCategory: {
              ...prev.byCategory,
              [gameCategory]: {
                ...categoryData,
                [enemyName]: {
                  ...enemyStats,
                  totalSpins: enemyStats.totalSpins + 1,
                  bigWins: isBigWin ? enemyStats.bigWins + 1 : enemyStats.bigWins,
                  commandCounts: updatedCommandCounts
                }
              }
            }
          };
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allReelsStopped, hasSpun, reelSymbols, targetIndexes, coins, consecutiveLosses, poopCount, coingetLongSoundRef, coingetSuperLongSoundRef, currentMondai, getMondaiFromCommands, matchedText, gameCategory]);

  // 旧ターミナルのタイピング/カーソル演出は撤去

  // コイン枚数に応じたリール再生成ロジックは無効化（固定のまま運用）

  // 勝敗判定（HP到達でフェーズ切替）: 即時ではなく少しディレイし、KWSK表示中は保留
  useEffect(() => {
    if (gamePhase !== 'battle') return;
    if (endTransitionTimerRef.current) {
      clearTimeout(endTransitionTimerRef.current);
      endTransitionTimerRef.current = null;
    }
    const targetPhase = enemyHP <= 0 ? 'win' : (playerHP <= 0 ? 'lose' : null);
    if (!targetPhase) return;

    // 放置モードで既にポップアップを出している場合は、ここでは何もしない
    if (playMode === 'auto' && showAutoPlayEndModal) return;

    const schedule = () => {
      endTransitionTimerRef.current = setTimeout(() => {
        if (showKwsk) {
          // KWSKを見ている間は遷移を保留し、少し後に再チェック
          schedule();
          return;
        }
        if (playMode === 'auto') {
          setAutoPlayEndTargetPhase(targetPhase);
          setShowAutoPlayEndModal(true);
          return;
        }
        setGamePhase(targetPhase);
      }, 1200);
    };
    schedule();
    return () => {
      if (endTransitionTimerRef.current) {
        clearTimeout(endTransitionTimerRef.current);
        endTransitionTimerRef.current = null;
      }
    };
  }, [gamePhase, enemyHP, playerHP, showKwsk, playMode, showAutoPlayEndModal]);

  const startSpin = () => {
    if (playerHP <= 0 || enemyHP <= 0) return;
    // 正解リール・KWSKの発光を必ず停止
    setEuphoriaActive(false);
    setIsCorrectAnswer(false);
      // アンティシペーション解除
      setAnticipation(false, null);
      // STARTを押すまでBGMを流さない仕様：ここで初回再生を試みる
      try {
        const bgm = bgmRef.current;
        if (bgm && bgmPlaying && bgm.paused) {
          bgm.play().catch(() => {});
        }
      } catch(_) {}
      setHasSpun(true); // リールが回転したことを記録
  setHighlightIndexes([]); // 次のSTARTでハイライトをリセット（前回の正解ハイライトを消す）
      setHighlightIsCorrect(null); // STARTでハイライト色状態もクリア
      // コイン枯渇で遊べなくなるのを防ぐため、消費しない
      playSfxFromPool(leverSoundPoolRef);
      
      // 実際のスロット機と同じ挙動：スピン開始時に最終的な停止結果を事前決定
      // ゲーム状態を渡して接戦バランスを調整
      const gameState = {
        playerHP,
        enemyHP,
        consecutiveLosses,
        currentMondai
      };
      const predeterminedStops = decideReelStopsAtSpinStart(reelSymbols, Math.random, gameState);
      setPredeterminedResults(predeterminedStops);
      
  // 事前決定ログは decideReelStopsAtSpinStart 内で出力済み
      
      // 初期のtargetIndexesはnullで初期化（停止ボタン押下時に事前決定された結果に設定される）
      setTargetIndexes([null, null, null]);
      setSpinning([true, true, true]); // 全てのリールを回転させる
      // setBet(false); // BETボタンが廃止されたため、この行は不要
  };

  // 手動/放置で「START後に問題を切り替える」挙動を統一
  const startSpinWithMondaiSwitch = () => {
    startSpin();
    // フラグが立っていれば問題切り替え（手動モードと同じタイミング）
    if (shouldSwitchMondai) {
      const mondais = getMondaiFromCommands();
      let filtered = mondais.filter(m => m !== currentMondai);
      if (filtered.length === 0) filtered = mondais;
      if (filtered.length > 0) {
        setCurrentMondai(filtered[Math.floor(Math.random() * filtered.length)]);
      }
      setShouldSwitchMondai(false);
    }
  };

  const stopReel = (index, stopIndex) => {
    // 最新の状態でガード：すでに停止済みなら何もしない（モバイルの二重タップ/遅延クリック対策）
    if (!spinningRef.current?.[index]) return;
    // 効果音を再生（ボタンクリック時の反応を良くするため、状態更新前に実行）
    if (spinningRef.current[index]) {
      playSfxFromPool(stopSoundPoolRef);
    }
    
    // リールの停止状態を更新（React状態更新）
    let nextSpinningSnapshot = null;
    setSpinning((prev) => {
      if (!prev[index]) return prev; // すでに停止済みなら変更無し
      const newSpinning = [...prev];
      newSpinning[index] = false; // 指定したリールを停止
      nextSpinningSnapshot = newSpinning;
      return newSpinning;
    });
    
    // 実際のスロット機と同じ挙動：停止ボタンは演出のみ、実際の停止位置は事前決定された結果を使用
    let nextTargetIndexesSnapshot = [...targetIndexes];
    if (predeterminedResults[index] !== null) {
      // 競合を避けるため、関数型更新で安全に設定
      setTargetIndexes((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [null, null, null];
        if (next[index] !== null) return prev; // すでに設定済みなら変更しない
        next[index] = predeterminedResults[index];
        nextTargetIndexesSnapshot = next;
        return next;
      });
      
      // 停止時の絵柄確認用ログ（デバッグ用）
      // console.log(`リール${index + 1} 停止: targetIndex=${predeterminedResults[index]} (事前決定結果)`);

      // このリールの可視(上/中/下)3行をログ出力
      try {
        // const tIndex = predeterminedResults[index];
        const symbols = reelSymbols[index];
        if (Array.isArray(symbols) && symbols.length > 0) {
          // const len = symbols.length; // ログ停止に伴い未使用
          // UI上: 中段が targetIndex
          // ログ停止に伴い未使用の絵柄参照も削除
          // const topSym = symbols[(tIndex - 1 + len) % len];
          // const midSym = symbols[tIndex % len];
          // const botSym = symbols[(tIndex + 1) % len];
          // console.groupCollapsed(`リール${index + 1} 停止: 可視絵柄 (targetIndex=${tIndex} → 中段)`);
          // ログ出力停止（停止時可視絵柄）
          // console.log('上段:', topSym?.text, ' / 中段:', midSym?.text, ' / 下段:', botSym?.text);
          console.groupEnd();
        }
      } catch (e) {
  // console.warn('可視絵柄ログ出力中に例外:', e);
      }
    }

    // アンティシペーション評価：2リール停止かつ残り1リールが“揃う可能性がある”場合
    try {
      const spinningArr = nextSpinningSnapshot ?? spinning.map((v,i)=> i===index ? false : v);
      const stoppedIdx = spinningArr.map((s,i)=> s?null:i).filter(i=>i!==null);
      const spinningIdx = spinningArr.findIndex(s=>s);
      if (stoppedIdx.length === 2 && spinningIdx !== -1) {
        const nextTargets = nextTargetIndexesSnapshot;
        const checkRowMatch = (row) => {
          // row: 0=上,1=中,2=下
          const [rA, rB] = stoppedIdx;
          const tA = nextTargets[rA];
          const tB = nextTargets[rB];
          if (tA == null || tB == null) return false;
          const lenA = reelSymbols[rA].length;
          const lenB = reelSymbols[rB].length;
          const idxA = row === 0 ? (tA - 1 + lenA) % lenA : (row === 1 ? tA % lenA : (tA + 1) % lenA);
          const idxB = row === 0 ? (tB - 1 + lenB) % lenB : (row === 1 ? tB % lenB : (tB + 1) % lenB);
          const symA = reelSymbols[rA][idxA]?.text;
          const symB = reelSymbols[rB][idxB]?.text;
          return symA && symB && symA === symB;
        };
        const hasTwoOfKind = checkRowMatch(0) || checkRowMatch(1) || checkRowMatch(2);
        if (hasTwoOfKind) {
          setAnticipation(true, spinningIdx);
        } else {
          setAnticipation(false, null);
        }
      } else {
        // 2停止以外はアンティシペーション解除
        setAnticipation(false, null);
      }
    } catch(_) {}
  };

  // 放置モード: 勝敗が出るまで START/STOP を自動で回す
  const autoPlayTimersRef = useRef([]);
  const startSpinFnRef = useRef(null);
  const stopReelFnRef = useRef(null);
  const playerHPRef = useRef(playerHP);
  const enemyHPRef = useRef(enemyHP);
  useEffect(() => { playerHPRef.current = playerHP; }, [playerHP]);
  useEffect(() => { enemyHPRef.current = enemyHP; }, [enemyHP]);
  useEffect(() => {
    startSpinFnRef.current = startSpinWithMondaiSwitch;
    stopReelFnRef.current = stopReel;
  });

  useEffect(() => {
    const clearTimers = () => {
      const timers = autoPlayTimersRef.current;
      if (Array.isArray(timers)) {
        timers.forEach((t) => { try { clearTimeout(t); } catch (_) {} });
      }
      autoPlayTimersRef.current = [];
    };

    if (playMode !== 'auto') { clearTimers(); return; }
    if (gamePhase !== 'battle') { clearTimers(); return; }
    if (!autoPlayEnabled) { clearTimers(); return; }
    // KWSK（詳しく）表示中はユーザー閲覧を優先し、自動操作を完全停止
    if (showKwsk) { clearTimers(); return; }

    let cancelled = false;

    // 速度変更で進行中の STOP 予約を消さないため、速度は ref から読む
    const scaleMs = (ms) => {
      const speed = autoPlaySpeedRef.current;
      const speedFactor = speed === 'slow' ? 1.6 : (speed === 'fast' ? 0.7 : 1.0);
      return Math.max(80, Math.round(ms * speedFactor));
    };

    const tick = () => {
      if (cancelled) return;
      if (gamePhaseRef.current !== 'battle') return;
      if (playerHPRef.current <= 0 || enemyHPRef.current <= 0) return;

      // 正解成立時のクールダウン（放置モードのみ）
      try {
        const now = Date.now();
        const pauseUntil = autoPlayPauseUntilRef.current || 0;
        if (pauseUntil > now) {
          const tPause = setTimeout(() => { if (!cancelled) tick(); }, Math.max(0, pauseUntil - now));
          autoPlayTimersRef.current.push(tPause);
          return;
        }
      } catch (_) {}

      const spinningNow = Array.isArray(spinningRef.current) ? spinningRef.current : null;
      const allStopped = Array.isArray(spinningNow) ? spinningNow.every((s) => !s) : false;

      if (allStopped) {
        // UI上でSTARTが「活性」になったのが見えてから自動押下したいので、少し遅延して開始
        const startDelayMs = scaleMs(220);
        const tStart = setTimeout(() => { try { if (!cancelled) startSpinFnRef.current?.(); } catch (_) {} }, startDelayMs);

        // START後に順にSTOP（擬似的なボタン操作）
        const t1 = setTimeout(() => { try { if (!cancelled) stopReelFnRef.current?.(0); } catch (_) {} }, startDelayMs + scaleMs(700));
        const t2 = setTimeout(() => { try { if (!cancelled) stopReelFnRef.current?.(1); } catch (_) {} }, startDelayMs + scaleMs(950));
        const t3 = setTimeout(() => { try { if (!cancelled) stopReelFnRef.current?.(2); } catch (_) {} }, startDelayMs + scaleMs(1200));

        // 判定/演出が走るのを待って次へ
        const tNext = setTimeout(() => { if (!cancelled) tick(); }, startDelayMs + scaleMs(1900));
        autoPlayTimersRef.current.push(tStart, t1, t2, t3, tNext);
      } else {
        const t = setTimeout(() => { if (!cancelled) tick(); }, scaleMs(400));
        autoPlayTimersRef.current.push(t);
      }
    };

    clearTimers();
    tick();
    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [playMode, gamePhase, autoPlayEnabled, showKwsk]);

  // 放置プレイを「即時停止」する（画面遷移などでバックグラウンド進行を防ぐ）
  const stopAutoPlayImmediately = React.useCallback(() => {
    try {
      const timers = autoPlayTimersRef.current;
      if (Array.isArray(timers)) {
        timers.forEach((t) => { try { clearTimeout(t); } catch (_) {} });
      }
      autoPlayTimersRef.current = [];
    } catch (_) {}

    try { autoPlayPauseUntilRef.current = 0; } catch (_) {}

    try { setAutoPlayEnabled(false); } catch (_) {}
    // ゲーム画面外でスピンが走り続けないように強制停止
    try { setSpinning([false, false, false]); } catch (_) {}
  }, []);

  // 画面遷移用の簡易UI（最小）
  // キーボードナビ用インデックス（カテゴリ & 敵）
  const [categoryKeyboardIndex, setCategoryKeyboardIndex] = useState(0);
  const [enemyKeyboardIndex, setEnemyKeyboardIndex] = useState(0);

  const renderPhaseOverlay = () => {
    const isDesktopForRectAd = (() => {
      try {
        return (
          typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)').matches
        );
      } catch (_) {
        return false;
      }
    })();

    const isMobileForRectAd = (() => {
      try {
        return (
          typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(max-width: 599px) and (hover: none) and (pointer: coarse)').matches
        );
      } catch (_) {
        return false;
      }
    })();

    // 0) カテゴリ選択：Start/Free Battle の前に表示
    if (!gameCategory && !pendingCategory) {
      const categories = [
        { id: 'linux', icon: '🐧', name: 'Linux', desc: 'コマンドライン' },
        { id: 'sql', icon: '🗄️', name: 'SQL', desc: 'データベース' },
        { id: 'git', icon: '🌿', name: 'git', desc: 'バージョン管理' }
      ];
      // キーボードナビ用インデックス（state が無ければ初期化）
      if (typeof window !== 'undefined' && window.__initCatIdx === undefined) {
        window.__initCatIdx = true; // 1度だけ初期化フラグ
      }
      return (
        <div className="selection-screen">
          <div className="selection-container">
            <h2 className="selection-title">カテゴリを選択</h2>
            <p className="selection-subtitle">学習するカテゴリを選んでください</p>
            <div className="category-grid">
              {categories.map((c, idx) => (
                <button
                  key={c.id}
                  className={`category-card ${idx === categoryKeyboardIndex ? 'kb-focus' : ''}`}
                  aria-label={c.name}
                  title={c.name}
                  data-cat-idx={idx}
                  onClick={() => {
                    try { playSfxNode(decideSoundRef.current); } catch (_) {}
                    // まずカテゴリを仮決定し、次にモード選択へ
                    setPendingCategory(c.id);
                    setCurrentEnemyIndex(1);
                  }}
                >
                  <div className="category-icon">{c.icon}</div>
                  <div className="category-name">{c.name}</div>
                  <div className="category-desc">{c.desc}</div>
                  {idx === categoryKeyboardIndex && (
                    <div style={{position:'absolute', top:8, right:10, fontSize:11, color:'rgba(0,255,170,0.9)'}}>↵ 決定</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // 0.5) モード選択（カテゴリ選択の直後）
    if (!gameCategory && pendingCategory) {
      return (
        <div className="selection-screen">
          <div className="selection-container">
            <h2 className="selection-title">モードを選択</h2>
            <p className="selection-subtitle">「手動」または「放置」を選んでください</p>
            <div className="category-grid">
              <button
                className="category-card"
                aria-label="手動"
                title="手動"
                onClick={() => {
                  try { playSfxNode(decideSoundRef.current); } catch (_) {}
                  setPlayMode('manual');
                  setShowAutoPlayStartModal(false);
                  setAutoPlayEnabled(true);
                  setGameCategory(pendingCategory);
                  setPendingCategory(null);
                }}
              >
                <div className="category-icon">🕹️</div>
                <div className="category-name">手動</div>
                <div className="category-desc">通常どおり操作</div>
              </button>

              <button
                className="category-card"
                aria-label="放置"
                title="放置"
                onClick={() => {
                  try { playSfxNode(decideSoundRef.current); } catch (_) {}
                  setPlayMode('auto');
                  setShowAutoPlayStartModal(true);
                  setAutoPlayEnabled(false);
                  setGameCategory(pendingCategory);
                  setPendingCategory(null);
                }}
              >
                <div className="category-icon">🤖</div>
                <div className="category-name">放置</div>
                <div className="category-desc">勝敗が出るまで自動</div>
              </button>
            </div>

            <div className="selection-actions">
              <button
                className="back-button"
                onClick={() => {
                  try { if (!document.body.classList.contains('sfx-off') && returnSoundRef.current) { returnSoundRef.current.pause(); returnSoundRef.current.currentTime = 0; returnSoundRef.current.play().catch(()=>{}); } } catch(_) {}
                  setPendingCategory(null);
                  setPlayMode(null);
                }}
              >
                戻る
              </button>
            </div>
          </div>
        </div>
      );
    }
    // タイトル/メニュー画面は廃止（カテゴリ選択→対戦相手選択へ直行）
    if (gamePhase === 'select') {
      return (
        <div className="selection-screen">
          <div className="selection-container">
            <h2 className="selection-title">対戦相手を選択</h2>
            <p className="selection-subtitle">難易度を選んでバトル開始</p>
            <div className="enemy-list">
              {availEnemies.map((e, idx) => {
                const diff = (i => {
                  if (i === 0) return { stars: '★★★', aria: '上級', color: '#ff4757' }; // hard
                  if (i === 1) return { stars: '★★', aria: '中級', color: '#ffa502' }; // mid
                  return { stars: '★', aria: '初級', color: '#2ed573' }; // easy
                })(idx);
                return (
                  <button
                    key={e.id}
                    className={`enemy-card ${idx === enemyKeyboardIndex ? 'kb-focus' : ''}`}
                    title={`難易度: ${diff.aria}`}
                    onClick={() => { try { playSfxNode(decideSoundRef.current); } catch(_) {}; setCurrentEnemyIndex(idx); resetForNewOpponent(); setPlayerHP(100); setEnemyHP(100); setPlayerDisplayHP(100); setEnemyDisplayHP(100); setGamePhase('settings'); }}
                  >
                    <div className="enemy-card-inner">
                      <div className="enemy-name">{e.displayName}</div>
                      <div className="enemy-difficulty" style={{ color: diff.color }}>
                        <span className="difficulty-stars">{diff.stars}</span>
                        <span className="difficulty-label">{diff.aria}</span>
                      </div>
                    </div>
                    {idx === enemyKeyboardIndex && (
                      <div style={{position:'absolute', top:6, right:10, fontSize:11, color:'rgba(255,200,0,0.9)'}}>↵ 決定</div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="selection-actions">
              <button className="back-button" onClick={() => { try { if (!document.body.classList.contains('sfx-off') && returnSoundRef.current) { returnSoundRef.current.pause(); returnSoundRef.current.currentTime = 0; returnSoundRef.current.play().catch(()=>{}); } } catch(_) {}; setGameCategory(null); }}>戻る</button>
            </div>
          </div>
        </div>
      );
    }

    // 設定確認画面
    if (gamePhase === 'settings') {
      const enemyName = currentEnemy?.displayName || 'unknown';
      const categoryData = stats.byCategory[gameCategory] || {};
      const currentStats = categoryData[enemyName] || {
        totalSpins: 0, totalWins: 0, totalLosses: 0, bigWins: 0,
        maxConsecutiveWins: 0, maxConsecutiveLosses: 0, poopExplosions: 0, commandCounts: {}
      };
      const winRate = currentStats.totalSpins > 0 ? ((currentStats.totalWins / currentStats.totalSpins) * 100).toFixed(1) : '0.0';
      
      return (
        <div className="selection-screen">
          <div className="selection-container settings-container">
            <h2 className="selection-title">設定確認 - {currentCategoryName} / {enemyName}</h2>
            
            <div className="settings-panels">
              {/* 簡易成績表示 */}
              <div className="settings-panel settings-panel-compact">
                <div className="settings-stats-grid">
                  <div className="settings-stat-box">
                    <div className="settings-stat-label">総スピン数</div>
                    <div className="settings-stat-value">{currentStats.totalSpins}</div>
                  </div>
                  <div className="settings-stat-box">
                    <div className="settings-stat-label">勝率</div>
                    <div className="settings-stat-value" style={{ color: parseFloat(winRate) >= 50 ? '#2ed573' : '#ff4757' }}>{winRate}%</div>
                  </div>
                  <div className="settings-stat-box">
                    <div className="settings-stat-label">3揃い</div>
                    <div className="settings-stat-value">{currentStats.bigWins}回</div>
                  </div>
                  <div className="settings-stat-box">
                    <div className="settings-stat-label">最大連勝</div>
                    <div className="settings-stat-value">{currentStats.maxConsecutiveWins}</div>
                  </div>
                  <div className="settings-stat-box">
                    <div className="settings-stat-label">💩爆発</div>
                    <div className="settings-stat-value" style={{ color: '#ff4757' }}>{currentStats.poopExplosions || 0}回</div>
                  </div>
                  <div className="settings-stat-box">
                    <div className="settings-stat-label">爆発率</div>
                    <div className="settings-stat-value" style={{ color: '#ff6b81' }}>
                      {currentStats.totalSpins > 0 ? ((currentStats.poopExplosions || 0) / currentStats.totalSpins * 100).toFixed(1) : '0.0'}%
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="selection-actions" style={{ marginTop: '20px' }}>
              <button className="back-button" onClick={() => setGamePhase('select')}>戻る</button>
              <button className="battle-start-button" onClick={() => { try { if (!document.body.classList.contains('sfx-off') && battleStartSoundRef.current) { battleStartSoundRef.current.pause(); battleStartSoundRef.current.currentTime = 0; battleStartSoundRef.current.play().catch(()=>{}); } } catch(_) {}; setGamePhase('intro'); }}>
                <span className="battle-start-text">確認完了</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (gamePhase === 'intro') {
      return (
        <div className="selection-screen">
          <div className="selection-container intro-container">
            <div className="intro-enemy-name">{currentEnemy?.displayName}</div>
            <div className="intro-message">{currentEnemy?.lines?.intro}</div>
            <button className="battle-start-button" onClick={() => { try { if (!document.body.classList.contains('sfx-off') && battleStartSoundRef.current) { battleStartSoundRef.current.pause(); battleStartSoundRef.current.currentTime = 0; battleStartSoundRef.current.play().catch(()=>{}); } } catch(_) {}; setPlayerHP(100); setEnemyHP(100); setPlayerDisplayHP(100); setEnemyDisplayHP(100); setShowAnimation(false); setGamePhase('battle'); }}>
              <span className="battle-start-text">⚔️ バトル開始 ⚔️</span>
            </button>
          </div>
        </div>
      );
    }
    if (gamePhase === 'win') {
      return (
        <div className="animation-overlay" style={{background:'rgba(0,0,0,0.85)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center'}}>
          <div style={{
            fontSize: 56,
            fontWeight: 900,
            color: '#00ff88',
            textShadow: '0 0 20px rgba(0, 255, 136, 0.8), 0 0 40px rgba(0, 255, 136, 0.4)',
            marginBottom: 20,
            animation: 'winPulse 1.5s ease-in-out infinite'
          }}>
            YOU WIN
          </div>

          <button 
            className="menu-accent-button" 
            style={{marginTop: 20}}
            onClick={() => setGameCategory(null)}
            tabIndex={-1}
            onFocus={(e) => { try { e.currentTarget.blur(); } catch (_) {} }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                try { e.preventDefault(); } catch (_) {}
                try { e.stopPropagation(); } catch (_) {}
              }
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                try { e.preventDefault(); } catch (_) {}
                try { e.stopPropagation(); } catch (_) {}
              }
            }}
          >
            カテゴリ選択へ
          </button>

          {isMobileForRectAd && (
            <div style={{ marginTop: 16 }}>
              <IMobileAdIframe
                title="広告（レクタングル）"
                width={300}
                height={250}
                tagHtml={`<script type="text/javascript" src="https://j.zucks.net.zimg.jp/j?f=716766"></script>`}
              />
            </div>
          )}

          {isDesktopForRectAd && (
            <div style={{ marginTop: 16 }}>
              <IMobileAdIframe
                title="広告（レクタングル）"
                width={300}
                height={250}
                tagHtml={`<div id="im-8e526eed00bb4879a0e3827518bbd73a">
  <script async src="https://imp-adedge.i-mobile.co.jp/script/v1/spot.js?20220104"></script>
  <script>(window.adsbyimobile=window.adsbyimobile||[]).push({pid:84413,mid:589796,asid:1920438,type:"banner",display:"inline",elementid:"im-8e526eed00bb4879a0e3827518bbd73a"})</script>
</div>`}
              />
            </div>
          )}
          <style>{`
            @keyframes winPulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
          `}</style>
        </div>
      );
    }
    if (gamePhase === 'lose') {
      return (
        <div className="animation-overlay" style={{background:'rgba(0,0,0,0.85)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center'}}>
          <div style={{
            fontSize: 56,
            fontWeight: 900,
            color: '#ff4444',
            textShadow: '0 0 20px rgba(255, 68, 68, 0.8), 0 0 40px rgba(255, 68, 68, 0.4)',
            marginBottom: 20,
            animation: 'loseFade 2s ease-in-out infinite'
          }}>
            YOU LOSE
          </div>

          <button 
            className="menu-accent-button" 
            style={{marginTop: 20}}
            onClick={() => setGameCategory(null)}
            tabIndex={-1}
            onFocus={(e) => { try { e.currentTarget.blur(); } catch (_) {} }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                try { e.preventDefault(); } catch (_) {}
                try { e.stopPropagation(); } catch (_) {}
              }
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                try { e.preventDefault(); } catch (_) {}
                try { e.stopPropagation(); } catch (_) {}
              }
            }}
          >
            カテゴリ選択へ
          </button>

          {isMobileForRectAd && (
            <div style={{ marginTop: 16 }}>
              <IMobileAdIframe
                title="広告（レクタングル）"
                width={300}
                height={250}
                tagHtml={`<script type="text/javascript" src="https://j.zucks.net.zimg.jp/j?f=716766"></script>`}
              />
            </div>
          )}

          {isDesktopForRectAd && (
            <div style={{ marginTop: 16 }}>
              <IMobileAdIframe
                title="広告（レクタングル）"
                width={300}
                height={250}
                tagHtml={`<div id="im-8e526eed00bb4879a0e3827518bbd73a">
  <script async src="https://imp-adedge.i-mobile.co.jp/script/v1/spot.js?20220104"></script>
  <script>(window.adsbyimobile=window.adsbyimobile||[]).push({pid:84413,mid:589796,asid:1920438,type:"banner",display:"inline",elementid:"im-8e526eed00bb4879a0e3827518bbd73a"})</script>
</div>`}
              />
            </div>
          )}
          <style>{`
            @keyframes loseFade {
              0%, 100% { opacity: 0.8; }
              50% { opacity: 1; }
            }
          `}</style>
        </div>
      );
    }
    if (gamePhase === 'ending') {
      return (
        <div className="animation-overlay" style={{background:'rgba(0,0,0,0.85)'}}>
          <div style={{ color:'#fff', textAlign:'center' }}>
            <div style={{ fontSize: 24, margin: '32px 0' }}>ゲーム終了</div>
            <button
              className="menu-accent-button"
              onClick={() => setGameCategory(null)}
              tabIndex={-1}
              onFocus={(e) => { try { e.currentTarget.blur(); } catch (_) {} }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  try { e.preventDefault(); } catch (_) {}
                  try { e.stopPropagation(); } catch (_) {}
                }
              }}
              onKeyUp={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  try { e.preventDefault(); } catch (_) {}
                  try { e.stopPropagation(); } catch (_) {}
                }
              }}
            >
              カテゴリ選択へ
            </button>
          </div>
        </div>
      );
    }
  };

  // カテゴリ選択キーボード操作（カテゴリ未選択時のみ）
  useEffect(() => {
    if (gameCategory || pendingCategory) return; // 既に選択済み/モード選択中なら無効
    const isDesktop = typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!isDesktop) return;

    const categories = ['linux','sql','git'];

    const onKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (gameCategory || pendingCategory) return;

      // 数字キー直接指定
      if (['1','2','3'].includes(e.key)) {
        e.preventDefault();
        const idx = parseInt(e.key,10)-1;
        if (idx >=0 && idx < categories.length) {
          setCategoryKeyboardIndex(idx);
        }
        return;
      }
      // グリッド(2列)に応じた矢印移動
  const cols = 2; // 2列グリッド
      const move = (current, key) => {
  const col = current % cols;
        if (key === 'ArrowRight') {
          if (col < cols - 1 && current + 1 < categories.length) return current + 1;
          return current; // 端は留まる
        }
        if (key === 'ArrowLeft') {
          if (col > 0) return current - 1;
          return current;
        }
        if (key === 'ArrowDown') {
          const target = current + cols;
          if (target < categories.length) return target;
          // 下が無い場合はそのまま（linux -> git を可能にするため col==0 特例で +2? 実際 linux idx0 + cols=2 -> git idx2 OK）
          return current;
        }
        if (key === 'ArrowUp') {
          const target = current - cols;
          if (target >= 0) return target;
          return current;
        }
        return current;
      };
      if (['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(e.key)) {
        e.preventDefault();
        setCategoryKeyboardIndex(i => move(i, e.key));
        return;
      }
      // Enter / Space で決定
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const id = categories[categoryKeyboardIndex];
        try { playSfxNode(decideSoundRef.current); } catch(_) {}
        setPendingCategory(id);
        setCurrentEnemyIndex(1);
        return;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('keydown', onKeyDown); };
  }, [gameCategory, pendingCategory, categoryKeyboardIndex, setPendingCategory, setCurrentEnemyIndex, playSfxNode]);

  // 敵選択キーボード操作（ゲームフェーズselectのみ）
  useEffect(() => {
    if (!gameCategory || gamePhase !== 'select') return;
    const isDesktop = typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!isDesktop) return;
    const onKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (gamePhase !== 'select') return;
      // 数字キー 1..N
      if (/^[1-9]$/.test(e.key)) {
        const num = parseInt(e.key,10) - 1;
        if (num >=0 && num < availEnemies.length) {
          e.preventDefault();
          setEnemyKeyboardIndex(num);
        }
        return;
      }
      // 上下で循環
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setEnemyKeyboardIndex(i => (i - 1 + availEnemies.length) % availEnemies.length);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setEnemyKeyboardIndex(i => (i + 1) % availEnemies.length);
        return;
      }
      // Enter / Space で決定
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const idx = enemyKeyboardIndex;
        if (idx >=0 && idx < availEnemies.length) {
          try { playSfxNode(decideSoundRef.current); } catch(_) {}
          setCurrentEnemyIndex(idx);
          resetForNewOpponent();
          setPlayerHP(100); setEnemyHP(100); setPlayerDisplayHP(100); setEnemyDisplayHP(100);
          setGamePhase('settings');
        }
        return;
      }
      // Backspace / Escape でカテゴリ選択に戻る
      if (e.key === 'Escape' || e.key === 'Backspace') {
        e.preventDefault();
        try { if (!document.body.classList.contains('sfx-off') && returnSoundRef.current) { returnSoundRef.current.pause(); returnSoundRef.current.currentTime = 0; returnSoundRef.current.play().catch(()=>{}); } } catch(_) {}
        setGameCategory(null);
        return;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('keydown', onKeyDown); };
  }, [gameCategory, gamePhase, enemyKeyboardIndex, availEnemies.length, setGameCategory, setGamePhase, setCurrentEnemyIndex, resetForNewOpponent, playSfxNode]);

  // 設定確認画面のキーボード操作（ゲームフェーズsettingsのみ）
  useEffect(() => {
    if (gamePhase !== 'settings') return;
    const isDesktop = typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!isDesktop) return;
    const onKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (gamePhase !== 'settings') return;
      // Enter / Space: 確認完了（→ intro）
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        try { playSfxNode(decideSoundRef.current); } catch (_) {}
        setGamePhase('intro');
        return;
      }
      // Escape / Backspace: 戻る（→ select）
      if (e.key === 'Escape' || e.key === 'Backspace') {
        e.preventDefault();
        setGamePhase('select');
        return;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('keydown', onKeyDown); };
  }, [gamePhase, playSfxNode]);

  // バトル開始（intro画面）キーボード操作
  useEffect(() => {
    if (gamePhase !== 'intro') return;
    const isDesktop = typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!isDesktop) return;
    const onKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (gamePhase !== 'intro') return;
      // Enter / Space でバトル開始
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        try { playSfxNode(decideSoundRef.current); } catch (_) {}
        setPlayerHP(100); setEnemyHP(100); setPlayerDisplayHP(100); setEnemyDisplayHP(100);
        setShowAnimation(false);
        setGamePhase('battle');
        return;
      }
      // Escape / Backspace で設定に戻る
      if (e.key === 'Escape' || e.key === 'Backspace') {
        e.preventDefault();
        setGamePhase('settings');
        return;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('keydown', onKeyDown); };
  }, [gamePhase, playSfxNode]);

  // kwsk（詳しく）オープン
  const openKwskDetails = () => {
    // highlight が正解由来でない場合は、直前の正解エントリを利用できるようにする
    if (highlightIsCorrect !== true && !lastCorrectEntry) return;
    // 音声再生（敵キャラ選択と同じ音）
    try { playSfxNode(decideSoundRef.current); } catch(_) {}

    // 優先順位: ハイライト中のコマンド -> matchedText -> lastCorrectEntry
    let entry = null;
    try {
      if (highlightIsCorrect === true) {
        const hi = highlightIndexes?.[0];
        if (hi != null && reelSymbols?.[0]?.[hi]?.text) {
          const cmdText = reelSymbols[0][hi].text;
          const normalized = String(cmdText).trim().toLowerCase();
          entry = yojijukugoList.find(it => it.text?.trim()?.toLowerCase() === normalized);
        }
      }
    } catch (_) { entry = null; }

    if (!entry && matchedText) {
      const normalized = String(matchedText).trim().toLowerCase();
      entry = yojijukugoList.find(it => it.text?.trim()?.toLowerCase() === normalized);
      if (!entry) {
        const firstToken = normalized.split(/\s+/)[0];
        entry = yojijukugoList.find(it => (it.text?.split(/\s+/)[0] || '').trim().toLowerCase() === firstToken);
      }
    }

    if (!entry && lastCorrectEntry) {
      entry = lastCorrectEntry;
    }

    if (!entry) return;
    setKwskEntry(entry);
    setShowKwsk(true);
  };

  // PWA(ホーム画面起動)時の起動アニメーション
  const [showLaunchAnim, setShowLaunchAnim] = useState(false);
  useEffect(() => {
    try {
      const isStandalone = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || (window.navigator && window.navigator.standalone === true);
      const shown = sessionStorage.getItem('gh_launch_shown') === '1';
      if (isStandalone && !shown) {
        setShowLaunchAnim(true);
        setTimeout(() => {
          setShowLaunchAnim(false);
          try { sessionStorage.setItem('gh_launch_shown', '1'); } catch (_) {}
        }, 1600);
      }
    } catch (_) {}
  }, []);

  // アプリ更新（サーバーから最新バージョンを取得）
  // eslint-disable-next-line no-unused-vars
  const updateAppNow = React.useCallback(async () => {
    // 1) HTMLをno-storeで取得してネットワーク到達性を確認
    try { await fetch('/index.html', { cache: 'no-store' }); } catch (_) {}
    // 2) 既存キャッシュをクリア
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (_) {}
    // 3) SWの更新とリロード（なければフォールバックリロード）
    let reloaded = false;
    const reloadOnce = () => {
      if (reloaded) return; reloaded = true;
      try { window.location.reload(); }
      catch (_) { try { window.location.href = `${window.location.pathname}?t=${Date.now()}`; } catch (_) {} }
    };
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        let posted = false;
        // 既にwaitingがいればSKIP_WAITING
        for (const reg of regs) {
          if (reg && reg.waiting) {
            try { reg.waiting.postMessage({ type: 'SKIP_WAITING' }); posted = true; } catch (_) {}
          }
        }
        // controllerchangeで新SWへ切替後にリロード
        const onCtrl = () => reloadOnce();
        try { navigator.serviceWorker.addEventListener('controllerchange', onCtrl, { once: true }); }
        catch (_) { navigator.serviceWorker.addEventListener('controllerchange', onCtrl); }
        // waitingが居ない場合はupdateを試す
        if (!posted) {
          for (const reg of regs) {
            try { await reg.update(); } catch (_) {}
            if (reg && reg.waiting) {
              try { reg.waiting.postMessage({ type: 'SKIP_WAITING' }); } catch (_) {}
              break;
            }
          }
        }
        // 念のためフォールバック・タイムアウト
        setTimeout(reloadOnce, 1200);
        return;
      }
    } catch (_) {}
    // SW未使用時フォールバック
    setTimeout(reloadOnce, 100);
  }, []);

  // パスベースのルーティングを利用（history API）
  const getRoute = () => {
    try {
      // PUBLIC_PATH が使われている場合は先頭のベースを取り除く
      let path = (window.location.pathname || '/').replace(/\/+/g, '/');
      const base = (PUBLIC_PATH || '').replace(/\/$/, '');
      if (base) {
        const baseNorm = base.startsWith('/') ? base : `/${base}`;
        if (path.startsWith(baseNorm)) path = path.slice(baseNorm.length) || '/';
      }
      // remove leading slash
      path = path.replace(/^\//, '');
      return path || '';
    } catch (_) { return ''; }
  };
  const [route, setRoute] = React.useState(getRoute());
  React.useEffect(() => {
    const onPop = () => setRoute(getRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // 放置プレイ中にゲーム(/play)以外の画面へ遷移したら、即座に自動進行を停止
  const prevRouteRef = React.useRef(route);
  React.useEffect(() => {
    const prev = prevRouteRef.current;
    prevRouteRef.current = route;

    if (prev === 'play' && route !== 'play' && playMode === 'auto') {
      stopAutoPlayImmediately();
    }
  }, [route, playMode, stopAutoPlayImmediately]);

  // Mobile viewport sizing: ensure game area leaves room for header + banner
  React.useEffect(() => {
    const updateViewportVars = () => {
      try {
        if (typeof document === 'undefined' || typeof window === 'undefined') return;
        const root = document.documentElement;
        root.style.setProperty('--app-height', `${window.innerHeight}px`);

        const headerEl = document.querySelector('.global-header');
        const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
        root.style.setProperty('--global-header-h', `${Math.round(headerH)}px`);

        const bannerEl = document.querySelector('.game-bottom-mobile-banner-ad');
        let bannerH = 0;
        if (bannerEl) {
          const display = window.getComputedStyle(bannerEl).display;
          if (display !== 'none') bannerH = bannerEl.getBoundingClientRect().height;
        }
        root.style.setProperty('--mobile-banner-h', `${Math.round(bannerH)}px`);

        const footerEl = document.querySelector('.global-footer');
        const footerH = footerEl ? footerEl.getBoundingClientRect().height : 0;
        root.style.setProperty('--global-footer-h', `${Math.round(footerH)}px`);
      } catch (_) {}
    };

    updateViewportVars();
    try { window.addEventListener('resize', updateViewportVars, { passive: true }); } catch (_) { window.addEventListener('resize', updateViewportVars); }
    window.addEventListener('orientationchange', updateViewportVars);
    return () => {
      window.removeEventListener('resize', updateViewportVars);
      window.removeEventListener('orientationchange', updateViewportVars);
    };
  }, [route]);

  // Animation panel layout vars: measure overlays so the enemy portrait can avoid being covered
  React.useEffect(() => {
    const panelEl = animationPanelRef.current;
    if (!panelEl) return;

    const apply = () => {
      try {
        const enemyH = enemyStatusRef.current ? enemyStatusRef.current.getBoundingClientRect().height : 0;
        const stackH = statusMondaiStackRef.current ? statusMondaiStackRef.current.getBoundingClientRect().height : 0;
        panelEl.style.setProperty('--enemy-status-h', `${Math.round(enemyH)}px`);
        panelEl.style.setProperty('--status-stack-h', `${Math.round(stackH)}px`);
      } catch (_) {}
    };

    apply();

    // Prefer ResizeObserver for dynamic layout changes
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => apply());
      try { if (enemyStatusRef.current) ro.observe(enemyStatusRef.current); } catch (_) {}
      try { if (statusMondaiStackRef.current) ro.observe(statusMondaiStackRef.current); } catch (_) {}
      try { ro.observe(panelEl); } catch (_) {}
      return () => {
        try { ro.disconnect(); } catch (_) {}
      };
    }

    // Fallback: update on resize/orientation
    const onResize = () => apply();
    try { window.addEventListener('resize', onResize, { passive: true }); } catch (_) { window.addEventListener('resize', onResize); }
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [route, gamePhase]);

  // パスが /play のときは必ずカテゴリ選択画面へ遷移させる
  React.useEffect(() => {
    try {
      if (route === 'play') {
        setGameCategory(null);
        setGamePhase('select');
      }
    } catch (_) {}
  }, [route]);

  // ルートごとのページを優先して表示（利用規約 / プライバシー / ライセンス / 運営元）
  if (route === 'terms' || route === 'privacy' || route === 'license' || route === 'company' || route === 'contact') {
    let PageComp = null;
    if (route === 'terms') PageComp = TermsPage;
    if (route === 'privacy') PageComp = PrivacyPage;
    if (route === 'license') PageComp = LicensePage;
    if (route === 'company') PageComp = CompanyPage;
    if (route === 'contact') PageComp = ContactPage;
    const Page = PageComp;
    return (
      <div className="app-root">
        <GlobalHeader />
        <main className={`container${a11yCvd ? ' cvd' : ''}${a11yLowStim ? ' low-stim' : ''}`} style={{maxWidth: '720px', margin: '0 auto', width: '100%'}}>
          <Page />
        </main>
        <GlobalFooter />
      </div>
    );
  }

  // シンプルな Home / HowTo ページを表示
  if (route === '' || route === 'how-to') {
    let PageComp = null;
    if (route === '') PageComp = HomePage;
    if (route === 'how-to') PageComp = HowToPage;
    const Page = PageComp;

    let isDesktopForTopBanner = false;
    try {
      isDesktopForTopBanner = !!(window && window.matchMedia && window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)').matches);
    } catch (_) { isDesktopForTopBanner = false; }

    let isMobileForTopBanner = false;
    try {
      isMobileForTopBanner = !!(
        window &&
        window.matchMedia &&
        window.matchMedia('(max-width: 599px) and (hover: none) and (pointer: coarse)').matches
      );
    } catch (_) { isMobileForTopBanner = false; }

    const homeTopBannerTagHtml = `<div id="im-9ac19f45d0744a3a9d37f4060b863161">
  <script async src="https://imp-adedge.i-mobile.co.jp/script/v1/spot.js?20220104"></script>
  <script>(window.adsbyimobile=window.adsbyimobile||[]).push({pid:84413,mid:589796,asid:1920528,type:"banner",display:"inline",elementid:"im-9ac19f45d0744a3a9d37f4060b863161"})</script>
</div>`;

    const howToTopBannerTagHtml = `<div id="im-39bd28e7b9a14f8c8ad352241a1ca52e">
  <script async src="https://imp-adedge.i-mobile.co.jp/script/v1/spot.js?20220104"></script>
  <script>(window.adsbyimobile=window.adsbyimobile||[]).push({pid:84413,mid:589796,asid:1920529,type:"banner",display:"inline",elementid:"im-39bd28e7b9a14f8c8ad352241a1ca52e"})</script>
</div>`;

    const topBannerTagHtml = route === 'how-to' ? howToTopBannerTagHtml : homeTopBannerTagHtml;

    const homeMobileTopBannerTagHtml = `<script type="text/javascript" src="https://j.zucks.net.zimg.jp/j?f=716713"></script>`;

    const howToMobileTopBannerTagHtml = `<script type="text/javascript" src="https://j.zucks.net.zimg.jp/j?f=716714"></script>`;

    const mobileTopBannerTagHtml = route === 'how-to' ? howToMobileTopBannerTagHtml : homeMobileTopBannerTagHtml;

    return (
      <div className="app-root">
        <GlobalHeader />
        {isMobileForTopBanner ? (
          <div className="top-mobile-banner-ad" aria-label="広告">
            <IMobileAdIframe
              title="広告"
              tagHtml={mobileTopBannerTagHtml}
              width={320}
              height={50}
            />
          </div>
        ) : null}
        {isDesktopForTopBanner ? (
          <div className="top-banner-ad" aria-label="広告">
            <IMobileAdIframe
              title="広告"
              tagHtml={topBannerTagHtml}
              width={728}
              height={90}
            />
          </div>
        ) : null}
        <main
          className={`container${a11yCvd ? ' cvd' : ''}${a11yLowStim ? ' low-stim' : ''}`}
          style={{ maxWidth: isDesktopForTopBanner ? '728px' : '720px', margin: '0 auto', width: '100%' }}
        >
          <Page />
        </main>
        <GlobalFooter />
      </div>
    );
  }

  return (
  <div className="app-root">
    <GlobalHeader />
    <div className="game-with-side-ads">
      {/* PC: 左側広告は表示しない（ただしレイアウト維持のため同幅スペーサーは残す） */}
      <aside className="game-side-ad game-side-ad--left" aria-hidden="true" />

      <div className={`container game-main${a11yCvd ? ' cvd' : ''}${a11yLowStim ? ' low-stim' : ''}`} style={{maxWidth: '400px', margin: '0 auto', width: '100%'}}>
    {/* GlobalMenuBar removed */}
    {/* hidden: kwskEntry is read here to avoid unused-var in CI */}
    <span style={{ display: 'none' }} aria-hidden>{kwskEntry ? '' : ''}</span>
      {/* ホーム画面(PWA)起動時のスプラッシュ/起動アニメーション */}
      {showLaunchAnim && (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#eaf2ff',
            textAlign: 'center',
          }}
        >
          <div className="launch-logo" aria-hidden style={{ fontSize: 48, lineHeight: 1, marginBottom: 8 }}>🧄🐹</div>
          <div className="launch-title" style={{ fontWeight: 800, fontSize: 22, letterSpacing: '0.02em' }}>ガーリックハムスター</div>
        </div>
      )}
      {renderPhaseOverlay()}

      {showAutoPlayStartModal ? (
        <div className="auto-play-start-modal-overlay" role="dialog" aria-modal="true" tabIndex={-1}>
          <div className="auto-play-start-modal">
            <div className="auto-play-start-modal-title">放置プレイを開始します。</div>
            <div className="auto-play-start-modal-actions">
              <button
                type="button"
                className="confirm-modal-btn"
                onClick={() => {
                  try { playSfxNode(decideSoundRef.current); } catch (_) {}
                  setShowAutoPlayStartModal(false);
                  setAutoPlayEnabled(true);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showAutoPlayEndModal ? (
        <div className="auto-play-end-modal-overlay" role="dialog" aria-modal="true" tabIndex={-1}>
          <div className="auto-play-end-modal">
            <div className="auto-play-end-modal-title">放置プレイ終了です</div>
            <div className="auto-play-end-modal-actions">
              <button
                type="button"
                className="confirm-modal-btn"
                onClick={() => {
                  try { playSfxNode(decideSoundRef.current); } catch (_) {}
                  const phase = autoPlayEndTargetPhase;
                  setShowAutoPlayEndModal(false);
                  setAutoPlayEndTargetPhase(null);
                  if (phase) setGamePhase(phase);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* KWSK 詳細オーバーレイ */}
      {showKwsk && kwskEntry && (
        <div className="animation-overlay kwsk-overlay">
            <div className="kwsk-card" role="dialog" aria-modal="true" aria-labelledby="kwsk-title" ref={kwskCardRef}>
            <div className="kwsk-header">
              <div id="kwsk-title" className="kwsk-title">{kwskEntry?.text}</div>
              <button className="kwsk-close" aria-label="閉じる" title="Backspace/Esc" onClick={() => { setShowKwsk(false); setKwskEntry(null); }}>×</button>
            </div>
            <div className="kwsk-accent" />
            {kwskEntry?.origin && (
              <div className="kwsk-section">
                <div className="kwsk-section-title">名前の由来は？</div>
                <div className="kwsk-section-body">{kwskEntry.origin}</div>
              </div>
            )}
            {kwskEntry?.usage && (
              <div className="kwsk-section">
                <div className="kwsk-section-title">いつ使う？</div>
                <div className="kwsk-section-body">
                  {kwskEntry.usage.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i !== kwskEntry.usage.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {kwskEntry?.missing_impact && (
              <div className="kwsk-section">
                <div className="kwsk-section-title">ないと何に困る？</div>
                <div className="kwsk-section-body">{kwskEntry.missing_impact}</div>
              </div>
            )}
            {Array.isArray(kwskEntry?.example) && kwskEntry.example.length > 0 && (
              <div className="kwsk-section">
                <div className="kwsk-section-title">使用例</div>
                <div className="kwsk-code">
                  {kwskEntry.example.map((line, i) => (
                    <div key={i} className={`kwsk-code-line ${line.startsWith('$') ? 'prompt' : ''}`}>{line}</div>
                  ))}
                </div>
              </div>
            )}
            {(() => {
              // コマンドの種別を判定してオプション情報を取得
              let optionDetail = null;
              const commandName = kwskEntry?.text;
              
              if (commandName) {
                // Linux/Git/Dockerの順に検索
                optionDetail = getLinuxOptions(commandName) || getGitOptions(commandName) || getDockerOptions(commandName);
              }
              
              if (!optionDetail) return null;
              
              return (
                <>
                  {optionDetail.defaultBehavior && (
                    <div className="kwsk-section">
                      <div className="kwsk-section-title">オプションなしの場合は？</div>
                      <div className="kwsk-section-body">{optionDetail.defaultBehavior}</div>
                    </div>
                  )}
                  {optionDetail.options && optionDetail.options.length > 0 && (
                    <div className="kwsk-section">
                      <div className="kwsk-section-title">よく使うオプション</div>
                      <div className="kwsk-options-list kwsk-section-body">
                        {optionDetail.options.map((opt, idx) => (
                          <div key={idx} className="kwsk-option-item">
                            <div className="kwsk-option-flag">{opt.flag}</div>
                            <div className="kwsk-option-origin">【由来】{opt.origin}</div>
                            <div className="kwsk-option-role">{opt.role}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {optionDetail.combos && optionDetail.combos.length > 0 && (
                    <div className="kwsk-section">
                      <div className="kwsk-section-title">鉄板の組み合わせ</div>
                      <div className="kwsk-combos-list kwsk-section-body">
                        {optionDetail.combos.map((combo, idx) => (
                          <div key={idx} className="kwsk-combo-item">
                            <div className="kwsk-combo-pattern">{combo.pattern}</div>
                            <div className="kwsk-combo-desc">{combo.desc}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
            {kwskEntry?.background && (
              <div className="kwsk-section">
                <div className="kwsk-section-title">どんな背景・歴史？</div>
                <div className="kwsk-section-body">{kwskEntry.background}</div>
              </div>
            )}
            {kwskEntry?.trivia && (
              <div className="kwsk-section">
                <div className="kwsk-section-title">豆知識・トリビア</div>
                <div className="kwsk-section-body">{kwskEntry.trivia}</div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* 枠の内側の光るパネル（不正解そろい時は抑制、アンティシペーション時は青い鼓動） */}
      <div className={`panel-inner top ${
        (isCountingUp && (euphoriaActive || isCorrectAnswer)) ? "counting-glow" : (euphoriaActive ? "win-glow" : (anticipationActive ? "anticipation-glow" : (suppressBorderGlow ? "" : "glowing")))
      }`}></div>
      <div className={`panel-inner bottom ${
        (isCountingUp && (euphoriaActive || isCorrectAnswer)) ? "counting-glow" : (euphoriaActive ? "win-glow" : (anticipationActive ? "anticipation-glow" : (suppressBorderGlow ? "" : "glowing")))
      }`}></div>
      <div className={`panel-inner left ${
        (isCountingUp && (euphoriaActive || isCorrectAnswer)) ? "counting-glow" : (euphoriaActive ? "win-glow" : (anticipationActive ? "anticipation-glow" : (suppressBorderGlow ? "" : "glowing")))
      }`}></div>
      <div className={`panel-inner right ${
        (isCountingUp && (euphoriaActive || isCorrectAnswer)) ? "counting-glow" : (euphoriaActive ? "win-glow" : (anticipationActive ? "anticipation-glow" : (suppressBorderGlow ? "" : "glowing")))
      }`}></div>

      {/* （この位置のリール筐体挿入は誤りだったため削除） */}

      {/* アニメーション表示欄 - 初代ポケモン風バトルレイアウト */}
      <div ref={animationPanelRef} className="animation-panel" style={{
        width: '100%',
        minHeight: 'var(--animation-panel-min-h, 400px)',
        backgroundColor: '#303438',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'stretch',
        color: '#e6e6e6',
        overflow: 'hidden',
        position: 'relative',
        borderRadius: '8px',
        marginBottom: '6px',
        fontSize: '24px',
        boxShadow: 'inset 0 0 15px rgba(0, 0, 0, 0.5)',
        padding: '4px 8px',
        boxSizing: 'border-box',
        textAlign: 'left'
      }}>
        {!!currentCategoryName && (
          <div
            className="current-category-badge"
            aria-label={`ジャンル: ${currentCategoryName}`}
            title={`ジャンル: ${currentCategoryName}`}
          >
            {currentCategoryName}
          </div>
        )}
        {/* 敵キャラクター画像（ポートレート） */}
        {currentEnemy && (
          <>
            <img
              alt={currentEnemy.displayName}
              className={`enemy-portrait ${enemyAttackAnim ? 'taking-damage' : ''}`}
              src={`${PUBLIC_PATH}/images/enemies/dragon_001.png`}
              onError={(e) => {
                try {
                  const img = e.currentTarget;
                  if (img.dataset.__fallbacked) return; // ワンショットで差し替え
                  img.dataset.__fallbacked = '1';
                  img.src = placeholderImg;
                  img.classList.add('centered');
                  try { console.debug('[App] image onError, replaced with bundled placeholder', img.src); } catch(_) {}
                } catch(_) {}
              }}
            />
            {/* 正解ヒット時（敵にダメージ）の攻撃エフェクト */}
            {attackAnimType === 'lightning' && (
              <div className={`enemy-lightning ${enemyAttackAnim ? 'strike' : ''}`} aria-hidden="true" />
            )}
            {attackAnimType === 'fire' && (
              <div className={`enemy-fire ${enemyAttackAnim ? 'burn' : ''}`} aria-hidden="true" />
            )}
            {attackAnimType === 'slash' && (
              <div className={`enemy-slash ${enemyAttackAnim ? 'cut' : ''}`} aria-hidden="true" />
            )}
            {attackAnimType === 'impact' && (
              <div className={`enemy-impact ${enemyAttackAnim ? 'hit' : ''}`} aria-hidden="true" />
            )}
            {/* 正解時の攻撃コマンド表示 */}
            {attackingCommand && (
              <div className="attacking-command-display">
                <div className="attacking-command-text">{attackingCommand}</div>
              </div>
            )}
            {/* HP演出オーバーレイ（赤/青リップル） */}
            {hpEffect && (
              <div className={`hp-overlay ${hpEffect}`} aria-hidden="true" />
            )}
          </>
        )}
        {/* 上段：敵ステータス（横幅いっぱい） */}
        <div ref={enemyStatusRef} style={{ position: 'absolute', top: 10, left: 10, right: 10 }} className={`${enemyAttackAnim ? 'enemy-attack-anim' : ''}${hpEffect === 'enemy-damage' ? ' hp-shake' : ''}`}>
          <div className="modern-status-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: '1 1 auto' }}>
                <span className="modern-status-label">HP</span>
                <div className="hp-bar modern-hp-bar">
                  <div
                    className={`hp-bar-inner modern-hp-bar-inner ${getHPNeonClass(enemyDisplayHP)}${enemyAttackAnim ? ' hp-damage' : ''}`}
                    style={{ 
                      width: `${Math.max(0, Math.min(100, enemyDisplayHP))}%`,
                      backgroundColor: getHPBarColor(enemyDisplayHP),
                      boxShadow: enemyAttackAnim ? undefined : getHPBarShadow(enemyDisplayHP)
                    }}
                  />
                  <div className="hp-bar-label modern-hp-label">{enemyHP} / 100</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 下段：味方ステータス + メッセージウィンドウ（隙間ゼロで積む） */}
        <div ref={statusMondaiStackRef} className={`status-mondai-stack`}>
          <div className={playerAttackAnim ? 'player-attack-anim' : ''}>
            <div className="modern-status-card">
              {/* HP表示 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span className="modern-status-label">HP</span>
                <div className="hp-bar modern-hp-bar">
                  <div
                    className={`hp-bar-inner modern-hp-bar-inner ${getHPNeonClass(playerDisplayHP)}${playerAttackAnim ? ' hp-damage' : ''}`}
                    style={{ 
                      width: `${Math.max(0, Math.min(100, playerDisplayHP))}%`,
                      backgroundColor: getHPBarColor(playerDisplayHP),
                      boxShadow: playerAttackAnim ? undefined : getHPBarShadow(playerDisplayHP)
                    }}
                  />
                  <div className="hp-bar-label modern-hp-label">{playerHP} / 100</div>
                </div>
              </div>

              {/* にんにくポイントとうんこマークカウント表示 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: 4 }}>
                {/* にんにくポイント */}
                <div className="modern-marker-group" role="group" aria-label={`にんにくポイント: ${Math.min(3, Math.max(0, patiencePoints))} / 3`}>
                  <div className="modern-marker-icon" style={{ color: '#ffa726' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                      <path d="M12 2C10.9 2 10 2.9 10 4C10 4.7 10.4 5.4 11 5.7V7C8.2 7 6 9.2 6 12C6 14.8 8.2 17 11 17H13C15.8 17 18 14.8 18 12C18 9.2 15.8 7 13 7V5.7C13.6 5.4 14 4.7 14 4C14 2.9 13.1 2 12 2M11 9H13C14.7 9 16 10.3 16 12C16 13.7 14.7 15 13 15H11C9.3 15 8 13.7 8 12C8 10.3 9.3 9 11 9Z"/>
                    </svg>
                  </div>
                  <div className="modern-marker-dots" aria-hidden="true">
                    {[0, 1, 2].map(i => (
                      <div
                        key={i}
                        className={`modern-marker-dot ${garlicBlinking ? 'marker-blink' : ''} ${(garlicBlinking || i < patiencePoints) ? 'active' : ''}`}
                      />
                    ))}
                  </div>
                </div>

                {/* うんこマークカウント */}
                <div className="modern-marker-group" role="group" aria-label={`うんこカウント: ${Math.min(3, Math.max(0, poopCount))} / 3`}>
                  <div className="modern-marker-icon" style={{ color: '#8d6e63' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                      <path d="M11 4C13.2 4 15 5.8 15 8C15 8.6 14.9 9.1 14.7 9.6C16 10.4 17 11.8 17 13.5C17 16 15 18 12.5 18H9.5C7 18 5 16 5 13.5C5 11.8 6 10.4 7.3 9.6C7.1 9.1 7 8.6 7 8C7 5.8 8.8 4 11 4M11 6C9.9 6 9 6.9 9 8C9 8.4 9.1 8.7 9.3 9C9.8 8.5 10.4 8.2 11 8.2C11.6 8.2 12.2 8.5 12.7 9C12.9 8.7 13 8.4 13 8C13 6.9 12.1 6 11 6M12.5 16C13.9 16 15 14.9 15 13.5C15 12.1 13.9 11 12.5 11H9.5C8.1 11 7 12.1 7 13.5C7 14.9 8.1 16 9.5 16H12.5Z"/>
                    </svg>
                  </div>
                  <div className="modern-marker-dots" aria-hidden="true">
                    {[0, 1, 2].map(i => (
                      <div
                        key={i}
                        className={`modern-marker-dot ${poopBlinking ? 'marker-blink' : ''} ${(poopBlinking || i < poopCount) ? 'active' : ''}`}
                        style={{
                          backgroundColor: (poopBlinking || i < poopCount) ? '#8d6e63' : 'rgba(255, 255, 255, 0.15)',
                          boxShadow: (poopBlinking || i < poopCount) ? '0 0 8px #8d6e63, 0 0 4px #8d6e63 inset' : 'none'
                        }}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                  {/* うんこ爆発エフェクト */}
                  {poopExploding && (
                    <div className="poop-explosion">
                      <div className="explosion-ring explosion-ring-1"></div>
                      <div className="explosion-ring explosion-ring-2"></div>
                      <div className="explosion-ring explosion-ring-3"></div>
                      <div className="explosion-flash"></div>
                      <div className="explosion-shockwave"></div>
                      <div className="explosion-particles">
                        {[...Array(16)].map((_, i) => (
                          <div key={i} className="explosion-particle" style={{ '--angle': `${i * 22.5}deg`, '--delay': `${i * 0.02}s` }}></div>
                        ))}
                      </div>
                      <div className="explosion-debris">
                        {[...Array(12)].map((_, i) => (
                          <div key={i} className="debris-piece" style={{ '--angle': `${i * 30}deg`, '--speed': `${0.8 + Math.random() * 0.4}` }}></div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* GPバー（コイン数バー）は非表示に変更 */}
            </div>
          </div>

          <div className={`mondai-window ${mondaiCorrectFlash ? 'mondai-correct-flash' : ''} ${mondaiSwitchBorderFlash ? 'mondai-switch-border-flash' : ''}`}>
            {currentMondai && (
              <span
                key={`mondai-switch-glow-${mondaiSwitchFxNonce}`}
                className="mondai-switch-glow"
                aria-hidden="true"
              />
            )}
            <div
              key={`mondai-text-${mondaiSwitchFxNonce}`}
              className={`mondai-text ${currentMondai ? 'mondai-pop' : ''} ${mondaiCorrectFlash ? 'mondai-correct-text-flash' : ''}`}
            >
              {currentMondai && currentMondai.replace(/(「|」|。)/g, "")}
            </div>
          </div>
        </div>

        {/* 青発光はオーバーレイ演出として重ねる */}

        {/* アニメーション用のスタイル */}
        <style>{`
          .player-attack-anim {
            animation: shake 0.3s, flash-red 0.3s;
          }
          @keyframes shake {
            0% { transform: translate(0, 0); }
            20% { transform: translate(-4px, 0); }
            40% { transform: translate(4px, 0); }
            60% { transform: translate(-4px, 0); }
            80% { transform: translate(4px, 0); }
            100% { transform: translate(0, 0); }
          }
          @keyframes flash-red {
            0% { box-shadow: 0 0 0 0 #ff3b3b; }
            50% { box-shadow: 0 0 16px 4px #ff3b3b; }
            100% { box-shadow: 0 0 0 0 #ff3b3b; }
          }
          .enemy-attack-anim {
            animation: shake 0.3s, flash-red 0.3s;
          }
          @keyframes pulseBlue {
            0% { transform: scale(0.95); opacity: 0.7; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(0.95); opacity: 0.7; }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
          
          @keyframes twinkle {
            0% { opacity: 0; transform: scale(0.1); }
            50% { opacity: 1; transform: scale(1); }
            100% { opacity: 0; transform: scale(0.1); }
          }
          .mondai-pop { animation: mondaiPop 380ms ease-out; }
          @keyframes mondaiPop {
            0% { transform: scale(0.985); filter: brightness(0.95); }
            60% { transform: scale(1.02); filter: brightness(1.05); }
            100% { transform: scale(1); filter: none; }
          }
        `}</style>
      </div>

      {/* リール部分の筐体（縦幅を狭く） */}
      <div
        className={`reel-window ${anticipationActive ? 'anticipation' : ''}`}
        style={{
          background: "linear-gradient(to bottom, #444, #222)",
          borderRadius: "10px",
          padding: "4px",
          boxShadow: "0 3px 10px rgba(0,0,0,0.5) inset",
          border: "1px solid #666",
          marginBottom: "4px",
          position: "relative",
          boxSizing: 'border-box',
        }}
      >
        {/* リールを3つ表示（バトルフェーズ以外は操作ガード） */}
        <div className="reels" style={{ display: 'flex', gap: 4 }}>
          {spinning.map((isSpinning, index) => (
            <div
              key={`${index}-${reelResetNonce}`}
              style={{
                flex: 1,
                border: '1px solid #888',
                borderRadius: '4px',
                overflow: 'hidden',
                boxShadow: '0 0 3px rgba(0, 0, 0, 0.5)',
                background: '#111',
                padding: 0,
              }}
            >
              <SlotReel
                spinning={gamePhase === 'battle' ? isSpinning : false}
                symbols={reelSymbols[index]}
                onStop={(stopIndex) => stopReel(index, stopIndex)}
                targetIndex={targetIndexes[index]}
                highlightIndexes={highlightIndexes[index] !== null ? [highlightIndexes[index]] : []}
                isBlueGlow={highlightIsCorrect === true}
                reelIndex={index}
                coins={index === 0 ? coins : null}
                payout={index === 1 ? payout : null}
                speedFactor={anticipationActive && anticipationReelIndex === index ? 5 : 9}
                gameCategory={gameCategory}
              />
            </div>
          ))}
        </div>

      {/* 中央ペイラインは削除 */}
      </div>



      {/* スタートボタンと停止ボタンのコンテナ */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "linear-gradient(to bottom, #555, #333)",
          borderRadius: "10px",
          padding: "6px 6px", // パディングをさらに小さく
          marginBottom: "6px", // マージンを小さく
          border: "2px solid #666", // ボーダーを薄く
          boxShadow: "0 3px 10px rgba(0,0,0,0.3)",
        }}
      >
        <div
          style={{
            position: "relative", // 相対位置指定に変更
            width: "100%",
            display: "flex",
            justifyContent: "center", // 中央に配置
            alignItems: "center", // 垂直方向の配置を中央に
            height: "50px", // 高さを固定
          }}
        >
          {/* 停止ボタンコンテナ - 3つのボタンを中央に配置（幅計測のためref付与） */}
          <div
            ref={stopButtonsContainerRef}
            style={{
              display: 'flex',
              gap: '1px',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              justifyContent: 'center'
            }}
          >
            {spinning.map((isSpinning, index) => (
              <button
                key={index}
                onPointerDown={(e) => {
                  // モバイルは onClick が遅延/取りこぼしやすいので、押下で即停止
                  try { e.preventDefault(); } catch (_) {}
                  try { e.stopPropagation(); } catch (_) {}
                  try { if (gamePhase !== 'battle') return; } catch (_) {}
                  if (playMode === 'auto') return;
                  stopReel(index);
                }}
                onClick={(e) => {
                  // キーボード操作(Enter/Space)のフォールバック：pointerdownが発火しないため
                  try {
                    if (e && typeof e.detail === 'number' && e.detail !== 0) return;
                  } catch (_) {}
                  try { if (gamePhase !== 'battle') return; } catch (_) {}
                  if (playMode === 'auto') return;
                  stopReel(index);
                }}
                disabled={!isSpinning || playMode === 'auto'}
                className={`stop-button ${isSpinning ? 'active' : ''}`}
                aria-label={`リール${index + 1}を停止`}
                title={`テンキー${index + 1}`}
              />
            ))}
          </div>

          {/* STARTボタン - 停止ボタンの左側 */}
          <button
            className="spin-button start-button neon-red"
            onClick={() => {
              if (gamePhase !== 'battle') return;
              if (playMode === 'auto') return;
              startSpinWithMondaiSwitch();
            }}
            // 放置モードでも「全停止したらSTARTが活性」に見えるよう、disabled条件からautoを外す
            disabled={!allReelsStopped || gamePhase !== 'battle'}
            title="Space"
            style={{
              padding: '5px 8px',
              fontSize: '10px',
              borderRadius: '14px',
              height: '26px',
              minWidth: '64px',
              position: 'absolute',
              left: '26px',
              top: 0,
              bottom: 0,
              margin: 'auto 0',
              zIndex: 2
            }}
          >
            START
          </button>

          {/* KWSKボタン - 停止ボタンの右側 */}
          {(() => {
            return (
          <button
            onClick={openKwskDetails}
            disabled={highlightIsCorrect !== true}
            title={highlightIsCorrect === true ? 'Enter' : '条件を満たすと有効になります'}
            aria-label="詳しく"
            className={`kwsk-button${highlightIsCorrect === true ? ' active' : ''}`}
            style={{
              position: 'absolute',
              right: '32px',
              top: 0,
              bottom: 0,
              margin: 'auto 0',
              zIndex: 2
            }}
          >kwsk</button>
            );
          })()}
        </div>
      </div>

      {/* コントロールパネルは要望により削除 */}

      {/* ポリシー情報モーダル */}
      {showPolicyModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setShowPolicyModal(false)}
        >
          <div
            style={{
              backgroundColor: '#2a2a2a',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '90vw',
              maxHeight: '80vh',
              overflow: 'auto',
              border: '2px solid #00d4ff',
              boxShadow: '0 4px 20px rgba(0, 212, 255, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {policyType === 'terms' && (
              <>
                <h2 style={{ color: '#00d4ff', marginTop: 0 }}>利用規約</h2>
                <div style={{ color: '#ccc', lineHeight: 1.6, fontSize: '14px' }}>
                  <p>本アプリケーション「ガーリックハムスター」（以下「本サービス」）をご利用いただきありがとうございます。</p>
                  <p><strong>第1条 総則</strong></p>
                  <p>本規約は、本サービスの利用条件を定めるものです。本サービスのご利用により、本規約に同意したものとみなされます。</p>
                  <p><strong>第2条 利用資格</strong></p>
                  <p>本サービスは、個人が学習・娯楽目的で使用できます。営利目的での使用は禁止します。</p>
                  <p><strong>第3条 禁止事項</strong></p>
                  <p>ユーザーは、以下の行為を禁止します：</p>
                  <ul>
                    <li>本サービスの改ざん、リバースエンジニアリング</li>
                    <li>他のユーザーへの嫌がらせ、不正な使用</li>
                    <li>本サービスのセキュリティの破壊</li>
                  </ul>
                  <p><strong>第4条 免責事項</strong></p>
                  <p>本サービスは「現状のまま」提供されます。運営者は、本サービスの利用に起因する損害について責任を負いません。</p>
                </div>
              </>
            )}
            {policyType === 'privacy' && (
              <>
                <h2 style={{ color: '#00d4ff', marginTop: 0 }}>プライバシーポリシー</h2>
                <div style={{ color: '#ccc', lineHeight: 1.6, fontSize: '14px' }}>
                  <p>本アプリケーションの個人情報の取扱いについて説明します。</p>
                  <p><strong>第1条 収集する情報</strong></p>
                  <p>本サービスは、以下の情報を収集する場合があります：</p>
                  <ul>
                    <li>ゲームプレイ記録（スコア、プレイ履歴）</li>
                    <li>デバイス情報（OS、ブラウザ種別）</li>
                    <li>ローカルストレージ内のデータ</li>
                  </ul>
                  <p><strong>第2条 情報の利用</strong></p>
                  <p>収集した情報は、以下の目的で利用します：</p>
                  <ul>
                    <li>本サービスの提供・改善</li>
                    <li>ユーザー体験の向上</li>
                    <li>不正使用の防止</li>
                  </ul>
                  <p><strong>第3条 情報の共有</strong></p>
                  <p>個人情報は、ユーザーの同意なく第三者に開示しません。ただし法律に基づく開示請求には応じます。</p>
                  <p><strong>第4条 データセキュリティ</strong></p>
                  <p>本サービスは、個人情報を保護するための適切な技術的措置を講じます。</p>
                </div>
              </>
            )}
            {policyType === 'reporting' && (
              <>
                <h2 style={{ color: '#00d4ff', marginTop: 0 }}>外部送信規律</h2>
                <div style={{ color: '#ccc', lineHeight: 1.6, fontSize: '14px' }}>
                  <p>本アプリケーションの外部サーバーへの通信について説明します。</p>
                  <p><strong>第1条 外部送信の対象</strong></p>
                  <p>本サービスは、以下の情報を外部サーバーに送信する場合があります：</p>
                  <ul>
                    <li>ゲーム統計情報（匿名化済み）</li>
                    <li>アプリバージョン情報</li>
                    <li>エラーレポート</li>
                  </ul>
                  <p><strong>第2条 送信先</strong></p>
                  <p>外部サーバーへの送信は、本サービスの運営・改善を目的とした指定サーバーのみです。</p>
                  <p><strong>第3条 ユーザーの権利</strong></p>
                  <p>ユーザーは、メニューの設定からデータ送信の有無を制御できます。</p>
                  <p><strong>第4条 透明性</strong></p>
                  <p>本サービスは、定期的にこの規律を見直し、最新の状況を反映します。</p>
                </div>
              </>
            )}
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setShowPolicyModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#00d4ff',
                  color: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ホバー/スワイプエリアのヒントインジケーター - ユーザー要望により削除
      {gamePhase === 'battle' && gameCategory && (
        <>
          <div className="menu-edge-indicator left" ... />
          <div className="menu-edge-indicator right" ... />
        </>
      )}
      */}
      </div>

      {/* 右側広告（放置モードでも表示） */}
      <aside
        className="game-side-ad game-side-ad--right"
        aria-label="広告"
      >
        <IMobileAdIframe
          title="広告（右）"
          tagHtml={`<div id="im-1963d3683e3f42b285051aaaaa756189">
  <script async src="https://imp-adedge.i-mobile.co.jp/script/v1/spot.js?20220104"></script>
  <script>(window.adsbyimobile=window.adsbyimobile||[]).push({pid:84413,mid:589796,asid:1920433,type:"banner",display:"inline",elementid:"im-1963d3683e3f42b285051aaaaa756189"})</script>
</div>`}
        />
      </aside>
    </div>

    {/* スマホ画面ではバナー広告を表示しません */}
    <GlobalFooter />
  </div>
  );
};

export default App;